<template>
  <v-card id="fullBox" :class="{ 'dark-theme-card': isDarkTheme }">
    <!--    <h1>&#45;&#45;STREAMING&#45;&#45;</h1>-->
    <v-snackbar
      v-model="chatbotV2Snackbar"
      color="primary"
      location="bottom end"
      :timeout="12000">
      <div>
        <strong>Try the new chatbot experience</strong><br>
        New, improved UI, file uploads, and saved conversations.
      </div>
      <template #actions>
        <v-btn :href="chatbotV2Url" variant="text">Open new chatbot</v-btn>
        <v-btn aria-label="Dismiss new chatbot notification" variant="text" @click="chatbotV2Snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
    <div v-if="initialLoadingComplete && isLanguageChanging" class="language-loading-overlay">
      <div class="language-loading-spinner">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p>{{ $t('message.changing_language') }}</p>
      </div>
    </div>
    <div v-if="connectingToWebsocket" class="language-loading-overlay">
      <div class="language-loading-spinner">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
        <p v-if="!isReconnecting">Connecting...</p>
        <p v-if="isReconnecting">Reconnecting... ({{ reconnectAttempts }}/{{ maxReconnectAttempts }})</p>
        <p v-if="isReconnecting" class="reconnect-subtext">Connection lost, attempting to restore...</p>
      </div>
    </div>
    <div id="messages" ref="chatbox">
      <v-card-text class="grow" id="growingChat">
        <section aria-label="Chat messages">
          <div
            id="chatbox"
            ref="chatbotContainer"
            class="chat-transcript">
            <qualtrics-survey
              v-if="messages.length===5"
              :chatbot-id="$route.query.botName"
              :survey-url="surveyUrl"
            ></qualtrics-survey>
            <div
              class="chat-message-list"
              :role="listSemanticsEnabled ? 'list' : null"
              @keydown="listSemanticsEnabled=false"
              >
              <div
                v-for="(message,index) in messages"
                :key="index"
                :ref="el => updateScroll(el,index)"
                :role="listSemanticsEnabled ? 'listitem' : null"
                class="chat-message-item"
                :tabindex="listSemanticsEnabled ? -1 : 0"
                :aria-labelledby="listSemanticsEnabled ? null : `message-${index}-author`"
                :aria-describedby="listSemanticsEnabled ? null : `message-${index}-content`"
                @keydown="handleMessageKeyNav"
                @click="handleMessageClick($event, message, index)">
                <p :class="[message.role==='bot' ? 'botText':'userText', {'dark-theme': isDarkTheme}]">
              <img
                v-if="message.role === 'bot' && (this.$route?.query?.botName || '').toLowerCase() === 'bob'"
                src="../assets/images/BOB.png"
                alt=""
                style="width: 30px; height: 30px; position: relative; top: 12px;"
              />
              <img
                v-else-if="message.role === 'bot' && (this.$route?.query?.botName || '').toLowerCase() === 'coed'"
                src="../assets/images/coed.png"
                alt=""
                style="width: 30px; height: 30px; position: relative; top: 12px;"
              />
              <img
                v-else-if="message.role==='bot' && message.isLastInSeries"
                src="../assets/images/testudo.png"
                alt=""
                style="width: 26px; height: 30px; position: relative; top: 12px;"
              />
                  <div
                    v-else-if="message.role === 'bot' && !message.isLastInSeries"
                    style="width: 26px; height: 30px; display: inline-block; position: relative; top: 12px;"
                    aria-hidden="true"
                  ></div>
                  <span
                    class="sr-only"
                    :id="`message-${index}-author`">
                    {{ message.role === 'bot' ? srBotLabel : srUserLabel }}
                  </span>
                  <div
                    class="message-bubble"
                    :id="`message-${index}-content`"
                    :style="message.role === 'bot' ? botMessageStyle : userMessageStyle">
                <!-- Tool Calls History -->
                <div v-if="message.role === 'bot' && message.toolCalls && message.toolCalls.length > 0" class="tool-calls-container" :class="{'dark-theme': isDarkTheme}">
                  <div class="tool-calls-header" @click="message.showToolCalls = !message.showToolCalls">
                    <span class="tool-calls-icon">🤖</span>
                    <span class="tool-calls-title">Agentic Actions ({{ message.toolCalls.length }})</span>
                    <span class="tool-calls-toggle" :class="{ 'expanded': message.showToolCalls }">▼</span>
                  </div>
                  <div v-if="message.showToolCalls" class="tool-calls-list">
                    <div v-for="(toolCall, toolIndex) in message.toolCalls" :key="toolIndex"
                         class="tool-call-item"
                         :class="{ 'tool-call-error': toolCall.status === 'error' }">
                      <div class="tool-call-content">
                        <span class="tool-call-text">{{ toolCall.content }}</span>
                        <div v-if="toolCall.status === 'error' && toolCall.errorMessage" class="tool-call-error-message">
                          {{ toolCall.errorMessage }}
                        </div>
                      </div>
                      <div class="tool-call-meta">
                        <span class="tool-call-time">{{ toolCall.timestamp }}</span>
                        <span v-if="toolCall.runtime" class="tool-call-runtime">({{ toolCall.runtime }})</span>
                        <span v-if="toolCall.status === 'error'" class="tool-call-status-error">❌</span>
                        <span v-else-if="toolCall.status === 'completed'" class="tool-call-status-success">✓</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Regular message content -->
                <div
                  v-if="!message.type"
                  v-dompurify-html="message.message"
                  class="message-content"
                  :class="{'dark-theme': isDarkTheme}"
                  style="white-space: normal; display: inline;">
                </div>

                <!-- Department sample questions (Vue template) -->
                <div v-else-if="message.type === 'deptSampleQuestions'" class="sample-questions-section">
                  <div>{{ $t('message.dept_sample_questions') }}</div>
                            <div class="sample-questions-list">

                              <button
                                v-for="(question, qIndex) in message.questions"
                                :key="qIndex"
                                class="sample-question-item"
                                @click="selectSampleQuestion(question)"
                                type="button">
                                <span class="question-text">{{ question }}</span>
                                <i class="fas fa-arrow-right question-indicator" aria-hidden="true"></i>
                              </button>
                  </div>
                </div>

                <!-- Optional login/logout (Vue template) -->
                <div v-else-if="message.type === 'optionalLogin'" class="optional-login-message">
                  <template v-if="!umName">
                    For a more personalized experience, please
                    <button class="sign-in-button" @click="handleOptionalLogin">Sign In</button>
                  </template>
                  <template v-else>
                    You are logged in as <strong>{{ umName }}</strong>.
                    <button class="sign-in-button" @click="handleOptionalLogout">Log Out</button>
                  </template>
                </div>

                <div
                  v-if="message.role==='bot' && !message.isGreeting"
                  class="rating-container d-flex justify-start">
                  <v-tooltip location="bottom" :text="$t('message.thumbsup')">
                    <template v-slot:activator="{ props }">
                      <button
                        v-bind="props"
                        class="thumb-up"
                        @click="toggleThumbsUp(message)"
                        :class="{ 'clicked': message.userClickedThumbsUp }"
                        aria-label="Rate message helpful"
                        :aria-pressed="message.userClickedThumbsUp"
                      >
                        <!-- <font-awesome-icon :icon="['fas', 'thumbs-up']" aria-label="Thumbs Up"/> -->
                        <ThumbsUp />
                      </button>
                    </template>
                  </v-tooltip>

                  <v-tooltip location="bottom" :text="$t('message.thumbsdown')">
                    <template v-slot:activator="{ props }">
                      <button
                        v-bind="props"
                        class="thumb-down"
                        @click="toggleThumbsDown(message, index)"
                        :class="{ 'clicked': message.userClickedThumbsDown }"
                        aria-label="Rate message not helpful"
                        :aria-pressed="message.userClickedThumbsDown"
                      >
                        <!-- <font-awesome-icon :icon="['fas', 'thumbs-down']" aria-label="Thumbs Down" /> -->
                         <ThumbsDown />
                      </button>
                    </template>
                  </v-tooltip>
                  <v-tooltip location="bottom" :text="$t('message.copy')">
                    <template v-slot:activator="{ props }">
                      <button
                        v-bind="props"
                        v-if="showCopyIcon"
                        class="copy"
                        aria-label="Copy message"
                        @click="copyResponseToClipboard(message.message)"
                        @keydown.enter="copyResponseToClipboard(message.message)"
                        tabindex="0">
                        <!-- <font-awesome-icon :icon="['fas', 'copy']" aria-label="Copy"/> -->
                        <ClipboardCopy />
                      </button>
                    </template>
                  </v-tooltip>

                  </div>

                <div v-if="message.showSources" class="sourceBox" :class="{'dark-theme': isDarkTheme}">
                  <div class="source-header">
                    <span class="source-title" :style="{ color: isDarkTheme ? '#E0E0E0' : 'inherit' }">{{ $t('message.sources') }}</span>
                    <button class="close-button" aria-label="Close sources" @click="message.showSources=false">
                      <font-awesome-icon :icon="['fas', 'circle-xmark']" aria-hidden="true" title="Close Sources" />
                    </button>
                  </div>
                  <template v-if="botType === 'academic'">
                    <div v-for="(source, sourceIndex) in prepareAllSourcesForDisplay(message)" :key="source.number">
                      <span class="source-number" :class="{'dark-theme': isDarkTheme}">[{{ source.number }}]</span>
                      <span
                        :class="[
                          'source-title',                         // normal text colour
                          { 'clickable': source.isLink || source.isVideo }  // turn blue only when it should act like a link
                        ]"
                        v-dompurify-html="source.title">
                      </span>
                      <font-awesome-icon v-if="source.isVideo" class="video-icon" :icon="['fas', 'video']" />
                      <font-awesome-icon v-if="source.isLink" class="doc-icon" :icon="['fas', 'arrow-up-right-from-square']" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="source-list" v-dompurify-html="message.displayedSource"></div>
                    <font-awesome-icon v-if="source.isVideo" class="video-icon" :icon="['fas', 'video']" />
                    <font-awesome-icon v-if="source.isLink" class="doc-icon" :icon="['fas', 'arrow-up-right-from-square']" />
                  </template>

                </div>

                <!-- Artifacts Section -->
                <div v-if="message.artifacts && message.artifacts.length > 0" class="artifacts-container">
                  {{ console.log('Rendering artifacts for message:', message.artifacts.length, 'artifacts') }}
                  <artifact
                    v-for="(artifact, index) in message.artifacts"
                    :key="index"
                    :artifact="artifact"
                    :class="{'dark-theme': isDarkTheme}"
                  />
                </div>

                <!-- AdaptiveCard Section -->
                <div v-if="message.adaptiveCardTemplate" class="adaptive-card-container">
                  <adaptive-card
                    :template="message.adaptiveCardTemplate"
                    :data="message.adaptiveCardData || null"
                    :debug="false"
                    @action="handleAdaptiveCardAction"
                  />
                </div>
            <div v-if="message.showFeedbackBox" class="feedback-box" :class="{'dark-theme': isDarkTheme}" :id="'feedback-box-' + index">
              <div class="feedback-content">
                <textarea
                  v-model="message.feedbackText"
                  :placeholder="getFeedbackPlaceholder()"
                  maxlength="200"
                  :id="'feedback-input-' + index"
                  aria-label="Your Feedback"
                  :aria-describedby="'feedback-error-' + index"
                  :style="feedbackInputStyle"
                ></textarea>
                <div v-if="message.feedbackError" class="error-message">{{ message.feedbackError }}</div>
                <div class="feedback-footer">
                  <span class="char-count">{{ 200 - message.feedbackText.length }} {{ $t('message.characters_left') }}</span>

                  <div class="email-notification">
                    <label class="checkbox-container">
                      <input type="checkbox" v-model="message.notifyOnResolution" aria-label="Notify me when resolved">
                      <span class="checkmark"></span>
                      {{ $t('message.notify') }}
                    </label>
                    <label :for="'user-email-' + index" class="visually-hidden">
                      Email Address
                    </label>
                    <input
                      v-if="message.notifyOnResolution"
                      type="email"
                      v-model="message.userEmail"
                      :placeholder="$t('message.enter_email')"
                      aria-label="Your Email"
                      class="email-input"
                    >
                  <div v-if="message.notifyOnResolution && message.emailError" role="alert" class="error-message">{{ message.emailError }}</div>
                  </div>

                  <button class="submit-button" @click="submitRating('bad', message)"  :disabled="message.isSubmittingFeedback" aria-label="Submit Feedback">
                    {{ $t('message.submit') }}
                  </button>
                </div>
              </div>
            </div>
            </div>

              <!-- Render visualization if present -->
              <VisualizationRenderer
                v-if="message.visualContent"
                :html-content="message.visualContent"
                :title="message.visualTitle || 'Graph'"
                :is-dark-theme="isDarkTheme"
                @code-copied="handleVisualizationCopied"
                @downloaded="handleVisualizationDownloaded"
              />

              <!-- <img
                v-if="message.role==='user'"
                :src="userImageSrc"
                alt="User Picture"
                aria-label="User Picture"
                style="width:22px;height:22px;position:relative;top:12px;"
              > -->
              <CircleUserRound
                v-if="message.role==='user'"
                :size="22"
                :class="{ 'dark-theme-icon': isDarkTheme }"
                class="user-icon"
              />

            </p>

            <div
              v-if="initialLoadingComplete && !firstQuestionAsked && isExpanded && botType === 'academic' && (index >= messages.length-1) "
              class="centered-content"
              @keydown="disableListSemantics">
              <v-container fluid class="content-wrapper">
                <v-row justify="center">
                  <v-col cols="12" sm="10" md="8" lg="6">
                    <h2 class="text-h5 text-sm-h4 text-md-h3 mb-4" :style="{ color: theme.global.current.value.colors.greetingText }">{{$t('message.hello')}}, {{umName}}</h2>

                    <div class="chat-input-container">
                      <chat-input
                        v-if="initialLoadingComplete"
                        v-model="currentMsg"
                        :max-length="Number(charLimit)"
                        input-id="chat-message"
                        sr-message-id="chat-message-messages"
                        form-aria-label="Your Message Form"
                        input-aria-label="Message Input Field"
                        :is-dark-theme="isDarkTheme"
                        @send-message="sendMessage"
                      />

                      <div class="chat-input-container"
                           v-if="message.role==='bot' && (index >= messages.length-1) && botType === 'academic' && sampleQuestions.length > 0 && sampleQuestionsVisible && isExpanded ">

                        <v-card class="sample-questions-card" :class="{'dark-theme': isDarkTheme}">
                          <v-col class="d-flex">
                            <div class="suggested-questions-container">
                              <div class="title-container">
                                <font-awesome-icon :icon="['fas', 'lightbulb']" class="suggested-questions-icon" aria-hidden="true" />
                                <span class="suggested-questions-title">{{ $t('message.suggested_questions') }}</span>
                              </div>
                              <div class="instructions" role="note">
                                {{ $t('message.navigation_instructions') }}
                              </div>
                            </div>
                            <v-spacer></v-spacer>
                            <button
                              @click="closeSampleQuestions"
                              @keydown.enter="closeSampleQuestions"
                              @keydown.space.prevent="closeSampleQuestions"
                              class="close-panel-button"
                              aria-label="Close suggested questions panel"
                              tabindex="0">
                              <font-awesome-icon :icon="['fas', 'circle-xmark']" aria-hidden="true" />
                              <span class="sr-only">Close suggested questions panel</span>
                            </button>
                          </v-col>

                          <v-list role="listbox" aria-label="Sample questions">
                            <v-list-item
                              v-for="(question, index) in sampleQuestions"
                              :key="index"
                              role="option"
                              @click="selectSampleQuestion(question.questionText)"
                            >
                              <v-list-item-title :style="{ color: isDarkTheme ? '#E0E0E0' : 'inherit' }">{{ getFirstWords(question.questionText, 5) }}</v-list-item-title>
                              <v-list-item-subtitle :style="{ color: isDarkTheme ? '#E0E0E0' : '#666666', opacity: '0.9' }">{{ getRemainingText(question.questionText, 5) }}</v-list-item-subtitle>
                            </v-list-item>
                          </v-list>
                        </v-card>
                      </div>
                    </div>

                  </v-col>
                </v-row>
              </v-container>
            </div>
          </div>
        </div>
        <div v-if="loading" class="typing-indicator" aria-label="Thinking" title="Thinking">
            <!-- <font-awesome-icon icon="fa-solid fa-spinner" spin aria-label="Thinking" title="Thinking"/> -->
            <img
              src="../assets/images/testudo.png"
              alt="Testudo Thinking"
              aria-hidden="true"
              class="thinking-testudo"
            />
            <span
              v-if="toolStatus"
              class="tool-status"
            >{{ toolStatus }}</span>
            <span class="dot" aria-hidden="true">.</span>
            <span class="dot" aria-hidden="true">.</span>
            <span class="dot" aria-hidden="true">.</span>
          </div>
          <ChatTextFormatter
            v-if="loading && messageBuffer"
            :content="messageBuffer"
            aria-live="off"
            aria-hidden="true"
            role="presentation"
          />
          </div>
        </section>
      </v-card-text>
    </div>

    <!--    <div v-if="this.sampleQuestions && this.sampleQuestions.length > 0 && !sampleQuestionsVisible" class="reopen-icon-container">-->
    <!--      <v-tooltip location="bottom" :text="$t('message.show_suggested')">-->
    <!--        <template v-slot:activator="{ props }">-->
    <!--          <button-->
    <!--            v-bind="props"-->
    <!--            @click="reopenSampleQuestions"-->
    <!--            @keydown.enter="reopenSampleQuestions"-->
    <!--            @keydown.space.prevent="reopenSampleQuestions"-->
    <!--            class="reopen-icon"-->
    <!--            aria-label="Show suggested questions"-->
    <!--            tabindex="0">-->
    <!--            <CircleHelp />-->
    <!--            <span class="sr-only">Show suggested questions</span>-->
    <!--          </button>-->
    <!--        </template>-->
    <!--      </v-tooltip>-->
    <!--    </div>-->

    <div v-if="this.sampleQuestions && this.sampleQuestions.length > 0 && (!sampleQuestionsVisible || sampleQuestionsDialogVisible)" class="reopen-icon-container">
      <v-tooltip location="bottom" :text="sampleQuestionsDialogVisible ? $t('message.hide_suggested') : $t('message.show_suggested')">
        <template v-slot:activator="{ props }">
          <button
            v-bind="props"
            @click="reopenSampleQuestions"
            @keydown.enter="reopenSampleQuestions"
            @keydown.space.prevent="reopenSampleQuestions"
            class="reopen-icon"
            :aria-label="sampleQuestionsDialogVisible ? 'Hide suggested questions' : 'Show suggested questions'"
            tabindex="0">
            <CircleHelp />
            <span class="sr-only">{{ sampleQuestionsDialogVisible ? 'Hide suggested questions' : 'Show suggested questions' }}</span>
          </button>
        </template>
      </v-tooltip>
    </div>

    <!-- Here is the normal chat input container -->
    <chat-input
      v-if="initialLoadingComplete && (firstQuestionAsked || !isExpanded || botType !== 'academic')"
      v-model="currentMsg"
      :max-length="Number(charLimit)"
      input-id="chat-message-2"
      sr-message-id="chat-message-2-messages"
      form-aria-label="Your Message Form"
      input-aria-label="Message input field"
      textbox-role="textbox"
      :is-dark-theme="isDarkTheme"
      @send-message="sendMessage"
    />

    <SampleQuestionsPopup
      :is-visible="sampleQuestionsDialogVisible"
      :questions="sampleQuestions"
      @close="closeSampleQuestions"
      @select="selectSampleQuestion"
    />

    <v-dialog
      v-model="showErrorDialog"
      persistent
      max-width="500"
      :retain-focus="false"
    >
      <v-card>
        <v-card-title class="headline error-title">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
          {{ $t('message.error') }}
        </v-card-title>
        <v-card-text>
          <p v-html="errorMessage"></p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" text @click="showErrorDialog = false">
            Dismiss
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div
      class="sr-only"
      :aria-live="listSemanticsEnabled ? 'polite' : 'assertive'"
      aria-atomic="true">
      {{ liveRegionText }}
    </div>

  </v-card>
</template>


<script>
import {createVNode, nextTick, render, inject, computed} from "vue";
import {escapeHtml} from "@vue/shared";
import InteractiveQuiz from './InteractiveQuiz.vue';
import showdown from 'showdown';
import showdownHighlight from 'showdown-highlight';
import katex from 'katex';
import splitAtDelimiters from "katex/contrib/auto-render/splitAtDelimiters";
import 'katex/dist/katex.min.css'
import 'highlight.js/styles/monokai.min.css'
import SampleQuestionsPopup from './SampleQuestionsPopup.vue';
import QualtricsSurvey from "@/components/QualtricsSurvey.vue";
import userLightImage from '../assets/images/user.png';
import userDarkImage from '../assets/images/user-dark.jpg';
import ChatInput from './ChatInput.vue'
import { useI18n } from 'vue-i18n';
import { ThumbsUp, ThumbsDown, ClipboardCopy, User, CircleUserRound, CircleHelp } from "lucide-vue-next";
import VisualizationRenderer from './visualizations/VisualizationRenderer.vue';
import {config} from "@/config/config";
import ChatTextFormatter from "@/components/ChatTextFormatter.vue";
import Artifact from "@/components/Artifact.vue";
import AdaptiveCard from "@/components/AdaptiveCard.vue";
import router from '@/router';
import { getClientVersionSnapshot } from "@/utils/versionInfo";

const converter = new showdown.Converter({
  extensions:[
    showdownHighlight({
      pre:true
    })
  ],
  simpleLineBreaks:true,
  disableForced4SpacesIndentedSublists:true,
  openLinksInNewWindow:true,
  literalMidWordUnderscores:true,
  tables:true
});

export default {
  components: {
    ChatTextFormatter,
    QualtricsSurvey,
    SampleQuestionsPopup,
    ChatInput,
    ThumbsUp,
    ThumbsDown,
    ClipboardCopy,
    User,
    CircleUserRound,
    CircleHelp,
    Artifact,
    AdaptiveCard,
    VisualizationRenderer
  },
  props: {
    isExpanded: {
      type: Boolean,
      default: true,
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  setup(props) {
    const theme = inject('theme')
    const { t } = useI18n();

    const botMessageStyle = computed(() => ({
      backgroundColor: theme.global.current.value.colors.botMessages,
      color: theme.global.current.value.colors.botText
    }))

    const userMessageStyle = computed(() => ({
      backgroundColor: theme.global.current.value.colors.userMessages,
      color: theme.global.current.value.colors.userText
    }))

    const feedbackBoxStyle = computed(() => ({
      backgroundColor: theme.global.current.value.colors.surface,
      color: theme.global.current.value.colors.text
    }))

    const feedbackInputStyle = computed(() => ({
      backgroundColor: theme.global.current.value.colors.background,
      color: theme.global.current.value.colors.text,
      borderColor: theme.global.current.value.colors.text
    }))

    const isDarkTheme = computed(() => theme.global.current.value.dark);

    const userImageSrc = computed(() =>
      isDarkTheme.value ? userDarkImage : userLightImage
    );


    return {
      theme,
      botMessageStyle,
      userMessageStyle,
      userImageSrc,
      feedbackBoxStyle,
      feedbackInputStyle,
      isDarkTheme,
      t
    }
  },

  data() {
    return {
      scrollElements:[],
      currentMsg: "",
      chats: ["UMD Virtual Assistant : Welcome"],
      token: "",
      client: null,
      conversation: null,
      username: "anonymous",
      from: "UMD Virtual Assistant",
      bot: 0,
      sessionId: null,
      loading: false,
      userClickedThumbsUp: false,
      userClickedThumbsDown: false,
      question: '',
      answer: '',
      isBotResponse: false,
      submittedPairs: [],
      callType: '',
      feedbackText: '',
      showFeedbackBox: false,
      feedbackSubmitted: false,
      questionId: "",
      showSources: false,
      source: 'Testing',
      messages:[],
      sources: '',
      botType: '',
      sampleQuestions: [],
      deptSampleQuestions: [],
      firstQuestionAsked: false,
      sampleQuestionsVisible: true,
      charLimit: 100,
      umName:'',
      optionalLogin: false,
      sampleQuestionsDialogVisible: false,
      initialLoadingComplete: false,
      notifyOnResolution: false,
      isSubmittingFeedback: false,
      userEmail: '',
      sessionEmail: '',
      feedbackError: '',
      emailError: '',
      surveyUrl:'',
      errorMessage: null,
      showErrorDialog: false,
      schemaNames: "",
      botNameMapping: {},
      currentLocale: 'en',
      chatbotV2Snackbar: false,
      chatbotV2Url: '',
      originalGreetings: {
        greeting: '',
        followup1: '',
        followup2: '',
        disclaimer: '',
        sampleQuestions: []
      },
      isLanguageChanging: false,
      socket: null,
      messageBuffer: "",
      pingIntervalId: null,
      connectingToWebsocket: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 3,
      reconnectTimeoutId: null,
      isReconnecting: false,
      wsUrl: null,
      toolStatus: "",
      toolStatusTimeout: null,
      artifactBuffer: [],
      currentToolCalls: [],
      activeToolExecutions: {}, // Track tool executions by execution_id
      liveRegionText: '',
      lastMessageElement: null,
      lastAnnouncementText: '',
      lastAnnouncementTimestamp: 0,
      liveRegionClearTimeout: null,
      listSemanticsEnabled:true,
    };
  },
  created() {
    this.announcementChain = Promise.resolve();
  },
  computed:{
    showInfoIcon(){
      return this.$route.query.botName?.toLowerCase() !== "panoptobotdemo"
    },
    showCopyIcon(){
      return this.$route.query.botName?.toLowerCase() !== "sfsc"
    },
    currChars(){
      return this.currentMsg.length;
    },
    srBotLabel() {
      const translated = typeof this.$t === 'function' ? this.$t('message.bot_label') : '';
      if (translated && translated !== 'message.bot_label') {
        return translated;
      }
      return 'Assistant';
    },
    srUserLabel() {
      const translated = typeof this.$t === 'function' ? this.$t('message.user_label') : '';
      if (translated && translated !== 'message.user_label') {
        return translated;
      }
      return 'You';
    },
    typingAnnouncement() {
      const translated = typeof this.$t === 'function' ? this.$t('message.typing_indicator') : '';
      if (translated && translated !== 'message.typing_indicator') {
        return translated;
      }
      return 'Assistant is typing...';
    }
  },
  watch: {
    loading(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.announceStatus(this.typingAnnouncement);
      }
    }
  },
  methods: {
    async setLanguageLoading(isLoading) {
      this.isLanguageChanging = isLoading;
      this.$emit('language-loading', isLoading);
    },
    async sendPing() {
      this.socket.send(JSON.stringify({
        type: "ping",
        data: "prevent timeout every 9 minutes"
      }));
    },

    async connectToWebSocket(botName, reconnect = false) {
      this.connectingToWebsocket = true;
      const wsEndpoint = config.wsEndpoint;
      let token = config.queryLambdaToken
      let jwt = localStorage.getItem("jwt-" + botName)
      if (jwt) {
        token = jwt
      }

      this.wsUrl = `wss://${wsEndpoint}/?botName=${botName}&token=${token}`;


      this.socket = new WebSocket(this.wsUrl);

      this.socket.addEventListener('open', async () => {
        console.log('WebSocket connected successfully');
        this.connectingToWebsocket = false;
        this.isReconnecting = false;
        this.reconnectAttempts = 0;

        if (!reconnect && this.socket.readyState === WebSocket.OPEN) {
          const clientVersion = await getClientVersionSnapshot();
          this.socket.send(JSON.stringify({
            type: "greeting",
            data: "initialize",
            clientVersion
          }));
          // Add a recurring ping since API Gateway closes after 10 quiet minutes
          this.pingIntervalId = setInterval(this.sendPing, 5 * 60 * 1000);
        }
      });

      this.socket.addEventListener('close', (e) => {
        console.log("WebSocket closed:", e.code, e.reason);
        this.connectingToWebsocket = false;
        clearInterval(this.pingIntervalId);

        // Check for authorization failures (AWS API Gateway WebSocket codes)
        if (e.code === 1002 || e.code === 1003) {
          console.log("WebSocket authorization failed:", e.code, e.reason);
          this.handleAuthorizationFailure(botName);
          return;
        }

        // Only attempt reconnection for unexpected closures
        if (e.code !== 1000 && !this.isReconnecting) {
          console.error("Bad status from websocket:", e);
          this.attemptReconnection();
        }
      });

      this.socket.addEventListener('error', (error) => {
        console.error('WebSocket error:', error);
        // For some browsers, auth failures come through as errors
        this.handleAuthorizationFailure(botName);
      });
      this.socket.addEventListener('message', (event) => {
        try {
          console.log("message received:", event);
          const data = JSON.parse(event.data);
          // Handle token messages (streaming)
          if (data.type === 'greeting') {
            this.handleGreeting(data.greeting);
          } else if (data.type === 'start') {
            console.log('WS: Starting new message, clearing artifact buffer and tool calls');
            this.loading = true;
            this.toolStatus = "";
            this.currentToolCalls = [];
            this.activeToolExecutions = {}; // Clear active tool executions
            this.clearToolStatusTimeout();
          } else if (data.type === 'end' || data.type === 'error') {
            console.log('WS: Message ended, attaching artifacts. Buffer size:', this.artifactBuffer.length);
            this.handleMessageResponse(data.response);
            this.attachBufferedArtifacts();
            this.attachToolCalls();
            this.loading = false;
            this.messageBuffer = "";
            this.toolStatus = "";
            this.clearToolStatusTimeout();
          } else if (data.type === 'token') {
            this.messageBuffer += data.content;
          } else if (data.type === 'tool') {
            this.handleToolMessage(data);
          } else if (data.type === 'artifact') {
            console.log('WS: Received artifact:', data.filename, data.filetype);
            this.handleArtifact(data);
          } else if (data.type === 'auth_upgraded') {
            console.log('WS: Auth upgraded successfully:', data.userId, data.userName);
          } else if (data.type === 'auth_upgrade_failed') {
            console.warn('WS: Auth upgrade failed:', data.reason);
          }
        } catch (error) {
          console.error('Error fetching greetings:', error);
          if (error.response && error.response.status === 404) {
            this.errorMessage = error.response.data.message || `Oops... something went wrong! Chatbot '${this.$route.query.botName}' not found. Please send an email to dit-ais@umd.edu to open a Service Now ticket to report this error.`;
            this.errorMessage = this.addHyperlinks(this.errorMessage)
            this.showErrorDialog = true;
          } else {
            this.errorMessage = "An error occurred while connecting to the chatbot. Please contact dit-ais@umd.edu if this issue persists or try again later.";
            this.showErrorDialog = true;
          }

          this.initialLoadingComplete = true;
          this.setLanguageLoading(false);
          return null;
        }
      });
    },
    // answerText: string with [^intent] footnotes
    // sourceMapByIntent: { [intent: string]: SourceInfo }
    normalizeCitations(answerText, sourceMapByIntent = {}) {
      if (typeof answerText !== "string") {
        return {text: answerText, sourceMap: sourceMapByIntent}
      }
      const intentToNumber = new Map(); // intent -> "1", "2", ...
      const sourceMap = {};             // "1" -> SourceInfo (numeric, ordered by first appearance)
      let next = 1;

      const text = answerText.replace(/\[\^([^\]]+)\]/g, (_, intent) => {
        // If the intent isn't in the map, you can choose to leave it as-is.
        // If you prefer to *always* number it, remove this guard.
        if (!(intent in sourceMapByIntent)) {
          console.warn(`Unmatched Intent: ${intent}`);
          return '';
          // return `[^${intent}]`; // keep unknown intents untouched
        }

        let n = intentToNumber.get(intent);
        if (!n) {
          n = String(next++);
          intentToNumber.set(intent, n);
          // build numeric map *right here* on first sight
          sourceMap[n] = sourceMapByIntent[intent];
        }
        return `[${n}]`;
      });

      return {text, sourceMap};
    },

    async handleGreeting(data) {
      this.setLanguageLoading(true);

      console.log("response: ", data)
      if (data.error === true) {
        this.errorMessage = data.message;
        this.showErrorDialog = true;
        this.initialLoadingComplete = true;
        return null;
      }

      // ADD this section to store schema display names:
      if (data.schemaDisplayNames) {
        this.botNameMapping = data.schemaDisplayNames;
        console.log("Schema display names loaded:", this.botNameMapping);
      }
      this.botType = data.botType;
      this.charLimit = data.charLimit || 100;
      this.umName = data.userName ? data.userName.split(' ')[0] : '';
      this.optionalLogin = data.optionalLogin || false;
      this.messages = []; // Clear any existing messages

      // Track locale for future changes
      const newLocale = this.$i18n.locale;
      this.currentLocale = newLocale;

      // First time loading - store the response data
      if (!this.originalGreetings.greeting) {
        this.originalGreetings = {
          greeting: data.greeting,
          followup1: data.followup1 || '',
          followup2: data.followup2 || '',
          disclaimer: data.disclaimer || '',
          sampleQuestions: data.sampleQuestions ?
            JSON.parse(JSON.stringify(data.sampleQuestions.questions)) : []
        };
      }

      // Set greeting with already translated data from backend
      await this.setGreeting(data);
      this.initialLoadingComplete = true;
      this.setLanguageLoading(false);
    },

    handleAuthorizationFailure(botName) {
      console.log("WebSocket authorization failed for bot:", botName);

      // Check if we have a stored JWT token
      if (localStorage.getItem("jwt-" + botName)) {
        console.log("Removing jwt and redirecting to login", botName);
        localStorage.removeItem("jwt-" + botName);
      }

      console.log("Going to login screen");
      router.push({
        path: '/login',
        query: { botName: botName, streaming: true }
      });
    },

    handleOptionalLogin() {
      const botName = this.$route.query.botName;
      if (!botName) return;

      // Check if already logged in
      const existingJwt = localStorage.getItem('jwt-' + botName);
      if (existingJwt && this.umName) {
        console.log('Already logged in, skipping popup');
        return;
      }

      let popup = null;
      let checkClosedInterval = null;
      let timeoutId = null;

      const cleanup = () => {
        if (checkClosedInterval) clearInterval(checkClosedInterval);
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        if (popup && !popup.closed) popup.close();
      };

      const messageHandler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type !== 'LOGIN_SUCCESS') return;

        const messageBotName = event.data.botName;
        if (event.data.jwt && event.data.username && messageBotName) {
          cleanup();

          // Store auth data
          localStorage.setItem('jwt-' + messageBotName, event.data.jwt);
          localStorage.setItem('username', event.data.username);

          // Send auth upgrade to existing websocket (preserves session)
          if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({
              type: 'auth_upgrade',
              token: event.data.jwt
            }));
          }

          // Update UI
          this.umName = event.data.username;

          // Refresh optional login message to show logged-in state
          const loginMsg = this.messages.find(m => m.type === 'optionalLogin');
          if (loginMsg) {
            loginMsg.message = this.buildOptionalLoginLabel(true);
          }

          // Add system message
          this.messageAdded(`You are now logged in as <strong>${event.data.username}</strong>. Please ask your question again.`, true, false);
        }
      };

      window.addEventListener('message', messageHandler);

      // Open popup
      const popupUrl = `${window.location.origin}${window.location.pathname}#/login?botName=${encodeURIComponent(botName)}`;
      popup = window.open(popupUrl, 'CAS Login', 'width=640,height=480,scrollbars=yes');

      if (!popup) {
        cleanup();
        // Fallback to redirect if popup blocked
        router.push({
          path: '/login',
          query: { botName, returnTo: this.$route.fullPath }
        });
        return;
      }

      // Detect popup close
      checkClosedInterval = setInterval(() => {
        if (popup.closed) cleanup();
      }, 500);

      // Timeout after 2 minutes
      timeoutId = setTimeout(cleanup, 120000);
    },

    handleOptionalLogout() {
      const botName = this.$route.query.botName;

      if (botName) {
        // Clear JWT and username
        localStorage.removeItem("jwt-" + botName);
        localStorage.removeItem("username");

        // Reload page to reset to unauthenticated state
        window.location.reload();
      }
    },

    getRandomQuestions() {
      let shuffledQuestions = [...this.sampleQuestions].sort(() => 0.5 - Math.random());
      return shuffledQuestions.slice(0, 5);
    },
    getChatbotV2Url() {
      const serviceHost = new URL(config.serviceURL || window.location.origin).hostname;
      const environment = serviceHost.includes('.dev.') ? '.dev' : serviceHost.includes('.qa.') ? '.qa' : '';
      const botName = encodeURIComponent(this.$route.query.botName || '');
      return `https://chatbot.aisolutions${environment}.umd.edu/bots/${botName}`;
    },
    shouldRedirectToChatbotV2(fetchedGreeting) {
      if (fetchedGreeting.chatbotV2 !== true || fetchedGreeting.botType !== 'academic') {
        return false;
      }
      try {
        return window.self === window.top;
      } catch (error) {
        return false;
      }
    },
    async setGreeting(fetchedGreeting) {
      // const fetchedGreeting = await this.fetchGreeting(botName);
      console.log(fetchedGreeting)
      console.log(fetchedGreeting.name)
      if (fetchedGreeting.greeting ) {
        this.messageAdded(fetchedGreeting.greeting, true, true);
      } else {
        // Default greeting if no match is found
        this.messageAdded(this.t('message.greeting'), true, true);
      }

      this.chatbotV2Url = fetchedGreeting.chatbotV2 === true ? this.getChatbotV2Url() : '';
      if (this.shouldRedirectToChatbotV2(fetchedGreeting)) {
        this.chatbotV2Snackbar = false;
        this.$emit('chatbot-v2-redirect', this.chatbotV2Url);
      } else {
        this.chatbotV2Snackbar = fetchedGreeting.chatbotV2 === true;
      }

      // Add optional login/logout message
      if (this.optionalLogin) {
        const optionalLoginSummary = this.buildOptionalLoginLabel(Boolean(this.umName));
        const optionalLoginMessage = {
          type: 'optionalLogin',
          message: '',
          role: 'bot',
          isGreeting: true,
          sources: {},
          showSources: false,
          questionId: '',
          question: '',
          answer: '',
          feedbackText: '',
          userEmail: '',
          notifyOnResolution: false,
          isLastInSeries: true,
          artifacts: [],
          toolCalls: [],
          showToolCalls: false
        };
        this.messages.push(optionalLoginMessage);
        this.announceStatus(optionalLoginSummary);
      }
      if (fetchedGreeting.followup1 && fetchedGreeting.followup1 != "") this.messageAdded(fetchedGreeting.followup1, true, true);
      if (fetchedGreeting.followup2 && fetchedGreeting.followup2 != "") this.messageAdded(fetchedGreeting.followup2, true, true);

      // Handle deptSampleQuestions - check if it's a JSON string (for AdaptiveCard)
      if (fetchedGreeting.deptSampleQuestions && typeof fetchedGreeting.deptSampleQuestions === 'string') {
        let isJsonCard = false;
        let parsedJson = null;

        // Try to parse as JSON first
        try {
          parsedJson = JSON.parse(fetchedGreeting.deptSampleQuestions);
          isJsonCard = true;
        } catch (e) {
          // Not JSON, handle as regular string
          isJsonCard = false;
        }

        if (isJsonCard && parsedJson) {
          // Add a message with the AdaptiveCard template
          const adaptiveCardMessage = {
            role: 'bot',
            message: '', // Empty message since the card will be displayed
            adaptiveCardTemplate: parsedJson,
            adaptiveCardData: null,
            isGreeting: true,
            isLastInSeries: false,
            showSources: false,
            showFeedbackBox: false,
            userClickedThumbsUp: false,
            userClickedThumbsDown: false,
            feedbackText: '',
            notifyOnResolution: false,
            userEmail: ''
          };

          this.messages.push(adaptiveCardMessage);
        } else {
          // Original handling for non-JSON deptSampleQuestions
          this.deptSampleQuestions = fetchedGreeting.deptSampleQuestions
            .split('\n')
            .filter(question => question.trim() !== '');

          // Add department sample questions as a separate bot message
          if (this.deptSampleQuestions.length > 0) {
            // Create HTML for the sample questions
            let sampleQuestionsHtml = `<div>${this.t('message.dept_sample_questions')}</div>
              <div class="sample-questions-list">`;

            this.deptSampleQuestions.forEach((question, index) => {
              sampleQuestionsHtml += `
                <div
                  class="sample-question-item"
                  data-question="${question.replace(/"/g, '&quot;')}"
                  data-index="${index}"
                  tabindex="0"
                  role="button"
                  aria-label="Ask: ${question.replace(/"/g, '&quot;')}">
                  <div class="question-text">${question}</div>
                  <i class="fas fa-arrow-right question-indicator" aria-hidden="true"></i>
                </div>
              `;
            });

            sampleQuestionsHtml += `</div>`;

            // Add it as a separate bot message
            // Add department sample questions as a separate bot message with special type
            const deptSampleSummary = this.buildSampleQuestionsLabel(this.deptSampleQuestions);
            const deptSampleMessage = {
              type: 'deptSampleQuestions',
              questions: this.deptSampleQuestions,
              message: '',
              role: 'bot',
              isGreeting: true,
              sources: {},
              showSources: false,
              questionId: '',
              question: '',
              answer: '',
              feedbackText: '',
              userEmail: '',
              notifyOnResolution: false,
              isLastInSeries: true,
              artifacts: [],
              toolCalls: [],
              showToolCalls: false
            };
            this.messages.push(deptSampleMessage);
            this.announceStatus(deptSampleSummary);
          }
        }
      }
      if (fetchedGreeting.name && fetchedGreeting.name != "") {
        console.log(fetchedGreeting.name)
        console.log(fetchedGreeting.botType)
        this.$emit('name-updated', fetchedGreeting.name);
      } else {
        if (fetchedGreeting.botType == "academic")
        {
          this.$emit('name-updated', "UMD Virtual Study Assistant");
        }
        else {
          this.$emit('name-updated', "UMD Virtual Agent");
        }
      }
      if (fetchedGreeting.disclaimer && fetchedGreeting.disclaimer != "") {
        console.log(fetchedGreeting.disclaimer)
        this.$emit('disclaimer-updated', fetchedGreeting.disclaimer);
      } else {
        this.$emit('disclaimer-updated', "I can make mistakes. Consider checking important information.");
      }
      this.$emit('banner-updated', fetchedGreeting.botUiBanner || null);
      if (fetchedGreeting.sampleQuestions && fetchedGreeting.sampleQuestions.questions) {
        let sampleQuestions = fetchedGreeting.sampleQuestions.questions;
        console.log("sample questions: ", sampleQuestions)
        this.sampleQuestions = fetchedGreeting.sampleQuestions.questions.filter((question)=>'Course Content Not Available'!==question.answerText);
        console.log("this sample: ", this.sampleQuestions)
        this.sampleQuestions = this.getRandomQuestions();
      } else {
        this.sampleQuestions = [];  // Clear out any previous sample questions if none are provided
      }
      if (fetchedGreeting.deptSampleQuestions && typeof fetchedGreeting.deptSampleQuestions === 'string') {
        this.deptSampleQuestions = fetchedGreeting.deptSampleQuestions
          .split('\n')
          .filter(question => question.trim() !== '');
      }
      this.surveyUrl = fetchedGreeting.surveyUrl
    },

    // Handle AdaptiveCard actions
    handleAdaptiveCardAction(payload) {
      console.log('Handling AdaptiveCard action:', payload);

      // Extract inputs from the payload
      const inputs = payload.inputs || {};
      const actionData = payload.data || {};

      // Helper function to interpolate template strings
      const interpolateTemplate = (template) => {
        if (!template || typeof template !== 'string') return template;

        // Replace ${variable} patterns with actual values from inputs
        return template.replace(/\$\{(\w+)\}/g, (match, key) => {
          return inputs[key] || match; // Use the input value or keep the original if not found
        });
      };

      // Check for userMessage field and interpolate it
      if (actionData.userMessage) {
        const interpolatedMessage = interpolateTemplate(actionData.userMessage);
        console.log('Interpolated userMessage:', interpolatedMessage);
        this.currentMsg = interpolatedMessage;
        this.sendMessage(interpolatedMessage);
        return;
      }

      // Handle login action
      if (actionData.action === 'login') {
        this.handleOptionalLogin();
        return;
      }

      // Check if there's a specific action type or data
      if (actionData.action === 'submit_question' || actionData.type === 'submit') {
        // Look for a question field in the action data first, then in inputs
        let question = actionData.question || inputs.question || inputs.text || inputs.message || '';

        // Interpolate the question if it contains template variables
        question = interpolateTemplate(question);

        if (question) {
          // Send the question as a message
          this.currentMsg = question;
          this.sendMessage(question);
        }
      } else if (actionData.action === 'select_topic' || actionData.topic) {
        // Handle topic selection
        let topic = actionData.topic || inputs.topic;
        topic = interpolateTemplate(topic);

        if (topic) {
          this.currentMsg = topic;
          this.sendMessage(topic);
        }
      } else if (actionData.question) {
        // Direct question in action data
        const question = interpolateTemplate(actionData.question);
        this.currentMsg = question;
        this.sendMessage(question);
      } else {
        // Generic handling - look for any text input
        const textValues = Object.values(inputs).filter(v => typeof v === 'string' && v.trim());
        if (textValues.length > 0) {
          // Send the first non-empty text value (interpolated if needed)
          const message = interpolateTemplate(textValues[0]);
          this.currentMsg = message;
          this.sendMessage(message);
        } else {
          console.log('No actionable data found in AdaptiveCard payload');
        }
      }
    },

    getFeedbackPlaceholder() {
      if (this.$route?.query?.botName?.toLowerCase() === "umdaipage") {
        return this.$t('message.multibot_feedback_placeholder');
      } else {
        return this.$t('message.feedback_placeholder');
      }
    },
    selectSampleQuestion(questionText) {
      // this.currentMsg = questionText;
      this.sendMessage(questionText);
      this.sampleQuestionsDialogVisible=false;

      this.$nextTick(() => {
        const textarea = this.$el.querySelector('textarea');
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);

        // Add color styling for dark mode
        if (this.isDarkTheme) {
          textarea.style.color = '#E0E0E0';
        }

      });
    },
    closeSampleQuestions() {
      this.sampleQuestionsVisible = false; // Set to true to hide the sample questions box
      this.sampleQuestionsDialogVisible=false;
    },
    reopenSampleQuestions() {
      if(!this.firstQuestionAsked) {
        // For the initial state, just toggle the inline sample questions
        this.sampleQuestionsVisible = !this.sampleQuestionsVisible;
      } else {
        // For after first question, toggle the dialog popup
        if (this.sampleQuestionsDialogVisible) {
          // If popup is open, close it using the same method as the close button
          this.closeSampleQuestions();
        } else {
          // If popup is closed, open it
          this.sampleQuestionsDialogVisible = true;
        }
      }
    },
    prepareAllSourcesForDisplay(message) {
      if (typeof message.sources === 'object' && message.sources !== null) {
        let sourceEntries = Object.entries(message.sources); // Convert object to array of entries
        return sourceEntries.map((entry, index) => {
          let number = index + 1;
          let source = entry[1];
          let title = "";
          if (source.title) {
            title = this.addHyperlinks(source.title);
          }
          let linkText = source.title.replace(/<[^>]*>/g, "");
          const videoTimestampRegex = /\d{2}:\d{2}:\d{2}/;
          let isVideo = videoTimestampRegex.test(linkText);
          let isLink = false;
          if (!isVideo) {
            const docLinkRegex = /<a\s+(?:.*?\s+)?href=["'](.*?)["'](.*?)>(.*?)\.(pdf|doc|docx|gdoc|gsheet|ppt|pptx)\b/i;
            isLink = docLinkRegex.test(title); // Check for links if it's not a video
          }
          return {
            number,
            title: title,
            isVideo,
            isLink
          };
        });
      } else {
        console.error('Expected an object for sources, received:', message.sources);
        return []; // Return an empty array
      }
    },

    appendQuiz(quizData, questionId) {
      let elId =`quiz_${questionId}`;
      this.messages.push({
        message: `<div id="${elId}"></div>`,
        role: 'bot',
        isGreeting: false,
        sources: [],
        showSources: false,
        questionId: questionId,
        question: '',
        answer: '',
        feedbackText: ''
      });

      //got the idea from https://stackoverflow.com/questions/76774836/vue-3-equivalent-of-vue-extend-mount
      this.$nextTick(() => {
        let oProps = {
          quizData: quizData
        }
        let vNode = createVNode(InteractiveQuiz, oProps); // MyComponent imported above
        vNode.appContext = this._.appContext; // magic sauce for the Vuetify issue. Thanks, Moritz Ringler!
        let el = document.getElementById(elId)
        // let el = this.$refs.chatbotContainer.appendChild(document.createElement('div'))
        render(vNode, el);
      });
    },

    handleSourceClick(number, message, index) {
      console.log("Bot type: ", this.botType)
      if (this.botType === 'academic') {
        let allSources = this.prepareAllSourcesForDisplay(message);
        message.displayedSource = allSources;
        message.showSources = true;
      } else {
        const source = message.sources[parseInt(number)];
        if (source) {
          let sourceText = '';
          if (source.title) {
            sourceText = `[${number}] ${this.addHyperlinks(source.title)}`;
          }
          let linkText = source.title.replace(/<[^>]*>/g, "");
          const videoTimestampRegex = /\d{2}:\d{2}:\d{2}/;
          let isVideo = videoTimestampRegex.test(linkText);
          source.isVideo = isVideo;
          let isLink = false;
          if (!isVideo) {
            const docLinkRegex = /<a\s+(?:.*?\s+)?href=["'](.*?)["'](.*?)>(.*?)\.(pdf|doc|docx|gdoc|gsheet|ppt|pptx)\b/i;
            isLink = docLinkRegex.test(sourceText); // Check for links if it's not a video
            source.isLink = isLink;
          }
          message.showSources = true;
          message.displayedSource = sourceText;

        }
        nextTick(() =>{
          if(message.showSources){
            let scroll = this.scrollElements[index];
            if(scroll){
              let sourceBox = scroll.querySelector('.sourceBox');
              this.scrollChatboxElementIntoView(sourceBox);
            }
          }
        })
      }
    },
    handleMessageClick(event, message, index) {
      const target = event.target;
      let numberElement = target.closest('.clickable-number');
      if (numberElement) {
        const number = numberElement.textContent.replace(/[^\d]/g, '');
        this.handleSourceClick(number, message, index);
      }
    },
    scrollChatboxElementIntoView(element, options = {}) {
      const chatbox = this.$refs.chatbox;
      if (!chatbox || !element) {
        return;
      }

      const { behavior = 'smooth', bottomPadding = 0 } = options;
      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;
      const visibleTop = chatbox.scrollTop;
      const visibleBottom = visibleTop + chatbox.clientHeight;

      if (elementTop < visibleTop) {
        chatbox.scrollTo({ top: elementTop, behavior });
      } else if (elementBottom > visibleBottom - bottomPadding) {
        chatbox.scrollTo({
          top: visibleTop + (elementBottom - (visibleBottom - bottomPadding)),
          behavior
        });
      }
    },
    sizing(){
      if(this.$route.query.size == "large"){
        document.getElementById("messages").style.height = "300px";
        document.getElementById("fullBox").style.height = "400px";
      }
      else{
        document.getElementById("fullBox").style.flexGrow = "1";
      }
    },
    // formatSources(text) {
    //   //Special formatting for source links in panoptobot
    //   if(this.$route.query.botName.toLowerCase() == "panoptobotdemo"){
    //     return text.replace(/((\n<a href.*<\/a>)+)/g,'<ul style="white-space:normal;"><span>Sources:</span>$1</ul>').replaceAll(/(\n<a href.*<\/a>)/g,'<li style="margin-left: 16px;">$1</li>');
    //   }
    //   // Step 1: Split the string into an array
    //   const sourcesArray = text.split(',');

    //   // Step 2 & 3: Trim and map each string to an `<li>` tag
    //   const sourcesString = sourcesArray.map(s => `<li style="margin-left: 16px;">${s.trim()} </li>`).join('\n');

    //   // Step 4: Optionally wrap in `<ul>` tags to form a complete list
    //   let formattedText = `<ul style="white-space:normal;">\n${sourcesString}\n</ul>`;
    //   formattedText = this.addHyperlinks(formattedText);
    //   return formattedText;
    // },

    addHyperlinks(text, sources) {
      try {
        if (typeof text !== 'string') {
          console.log('addHyperlinks called with non-string:', text);
          throw new TypeError('Input must be a string');
        }
        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;

        // Process text nodes only (avoid modifying text inside anchor tags)
        const processTextNodes = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            // Only process text nodes (not inside anchor tags)
            let content = node.textContent;
            let modified = false;

            // Process URLs - more specific to avoid matching time formats like a.m. and p.m.
            // This regex looks for domains with proper TLDs and requires either http(s):// prefix or www.
            const urlRegex = /((?:https?:\/\/|www\.)[a-zA-Z0-9][-a-zA-Z0-9]*(?:\.[a-zA-Z0-9][-a-zA-Z0-9]*)+(?:\/[^\s\[\]()<>{}]*)?)/gi;

            content = content.replace(urlRegex, (match) => {
              // Skip if this looks like a time format (a.m. or p.m.)
              if (/\b[ap]\.m\./i.test(match)) {
                return match;
              }

              // Make sure URL has http:// or https:// prefix
              const url = match.startsWith('http') ? match : `https://${match}`;
              modified = true;
              return `<a href="${url}">${match}</a>`;
            });

            // Process phone numbers
            const phoneRegex = /(\b1?-?\d{3}[-.\s]\d{3}[-.\s]\d{4}\b|\b\d[-.\s]?\d{3}[-.\s]\d{3}[-.\s]\d{4}\b)/g;
            content = content.replace(phoneRegex, (match) => {
              modified = true;
              return `<a href="tel:${match}">${match}</a>`;
            });

            // Process emails
            const emailRegex = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;
            content = content.replace(emailRegex, (match) => {
              modified = true;
              return `<a href="mailto:${match}">${match}</a>`;
            });

            // Process source numbers
            const bracketedNumberRegex = /[\s\n]*\[(\d+)\]/g;
            content = content.replace(bracketedNumberRegex, (match, num) => {
              modified = true;
              return `<span class="clickable-number">[${num}]</span>`;
            });

            // Replace the text node if we modified it
            if (modified) {
              const newNode = document.createElement('span');
              newNode.innerHTML = content;
              node.parentNode.replaceChild(newNode, node);
            }
          } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== 'A') {
            // Process children recursively for all non-anchor elements
            Array.from(node.childNodes).forEach(processTextNodes);
          }
        };

        // Process all text nodes in the document
        Array.from(tempDiv.childNodes).forEach(processTextNodes);

        // Handle MD source links if sources are provided
        if (sources) {
          const links = tempDiv.querySelectorAll('a');
          links.forEach(link => {
            const text = link.textContent;
            const href = link.getAttribute('href');

            // Check if this is a markdown-style source link
            const mdSourceMatch = text.match(/\[(\d+)\]/);
            if (mdSourceMatch) {
              const sourceNumber = parseInt(mdSourceMatch[1]);
              if (sourceNumber > 0 && sourceNumber <= sources.length) {
                const sourceTitle = sources[sourceNumber - 1].title;
                const sourceLink = sourceTitle.replace(/^<a href="([^"]+)".*/,"$1");
                console.log("Replacing Source Link:" + href + " with: " + sourceLink);
                link.setAttribute('href', sourceLink);
              }
            }
          });
        }

        return tempDiv.innerHTML;
      } catch (error) {
        console.log('Error in addHyperlinks:', error);
        // Return the original text if there is an error
        return text;
      }
    },
    maskNineDigitNumbers(text) {
      const botName = new URLSearchParams(window.location.search).get('botName') || ""
      if (botName.startsWith("D_ADVISE") || botName === "D_DSE_MULTIBOT") {
        return text;
      }
      // const nineDigitNumberRegex = /\d{3}-\d{2}-\d{4}|\d{9}/g;
      const nineDigitNumberRegex = /(?<!href=["']?)(\d{3}-\d{2}-\d{4}|\b\d{9}\b)(?!["'>])/g;
      const maskedText = text.replace(nineDigitNumberRegex, '*********');
      return maskedText;
    },
    toggleSourcesVisibility(message, index){
      message.showSources=!message.showSources;
      nextTick(() =>{
        if(message.showSources){
          let scroll = this.scrollElements[index];
          if(scroll){
            let sourceBox = scroll.querySelector('.sourceBox');
            this.scrollChatboxElementIntoView(sourceBox);
          }
        }
      })

    },
    stripHtml(html) {
      if (html === null || html === undefined) {
        return '';
      }
      // Create a temporary element
      const tempElement = document.createElement('div');
      // Set the HTML content
      tempElement.innerHTML = html;

      // Replace <br> tags with newlines
      const brElements = tempElement.querySelectorAll('br');
      brElements.forEach(br => br.replaceWith('\n'));

      // Replace <p> and other block elements endings with double newlines
      const blockElements = tempElement.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li');
      blockElements.forEach(el => {
        if (el.nextSibling) {
          el.insertAdjacentHTML('afterend', '\n');
        }
      });

      // Get the text content (this automatically removes all HTML tags)
      let text = tempElement.textContent || tempElement.innerText || '';

      // Decode HTML entities
      text = text.replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

      // Clean up extra whitespace
      text = text.replace(/\n\s*\n\s*\n/g, '\n\n')  // Replace triple+ newlines with double
        .replace(/^\s+|\s+$/g, '');        // Trim whitespace

      return text;
    },
    copyResponseToClipboard(messageText) {
      if (messageText) {
        const plainText = this.stripHtml(messageText);
        navigator.clipboard.writeText(plainText)
          .then(() => {
            console.log('Text copied to clipboard:', plainText);
            // Optionally, show a notification that the text was copied
          })
          .catch((err) => {
            console.error('Unable to copy text to clipboard:', err);
          });
      }
    },
    updateScroll(el,index){
      if (!el) {
        return;
      }
      this.scrollElements[index] = el;
      if (index === this.messages.length - 1) {
        this.lastMessageElement = el;
      }
    },
    handleMessageKeyNav(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      event.preventDefault();
      const sibling = event.key === 'ArrowDown'
        ? event.currentTarget?.nextElementSibling
        : event.currentTarget?.previousElementSibling;
      sibling?.focus();
    },
    disableListSemantics() {
      if (this.listSemanticsEnabled) {
        this.listSemanticsEnabled = false;
      }
    },

    // Method to handle proper scroll management when feedback box appears
    handleFeedbackBoxScroll() {
      this.$nextTick(() => {
        const chatbox = this.$refs.chatbox;
        if (chatbox) {
          // Smoothly scroll to maintain proper positioning
          chatbox.scrollTo({
            top: chatbox.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    },
    toggleThumbsUp(message) {
      //only use the top portion of code if you want to be able to toggle colors but still only submit one rating
      // this.userClickedThumbsUp = !this.userClickedThumbsUp;
      // this.userClickedThumbsDown = false;
      // this.submitRating('good');
      console.log("toggleThumbsUp called");
      if (!message.userClickedThumbsUp && !message.userClickedThumbsDown) {
        // If neither thumbs-up nor thumbs-down is selected, allow selection.
        message.userClickedThumbsUp = true;
        this.submitRating('good', message);
      }
    },
    toggleThumbsDown(message, index) {
      // this.userClickedThumbsDown = !this.userClickedThumbsDown;
      // this.userClickedThumbsUp = false;
      // this.submitRating('bad');
      console.log("toggleThumbsDown called");
      if (!message.userClickedThumbsUp && !message.userClickedThumbsDown) {
        // If neither thumbs-up nor thumbs-down is selected, allow selection.
        message.userClickedThumbsDown = true;
        if (message.question !== '' && message.answer !== '') {
          message.showFeedbackBox = true;

          if (this.sessionEmail) {
            message.userEmail = this.sessionEmail;
            message.notifyOnResolution = true;
          }

          // this.submitRating('bad');
          this.$nextTick(() => {
            const feedbackBox = document.getElementById('feedback-box-' + index);
            if (feedbackBox) {
              this.scrollChatboxElementIntoView(feedbackBox, {
                behavior: 'smooth',
                bottomPadding: 20
              });

              // Also ensure the chat container maintains proper positioning
              setTimeout(() => {
                const chatbox = this.$refs.chatbox;
                if (chatbox) {
                  // Check if we need to adjust scroll to keep input visible
                  const chatboxRect = chatbox.getBoundingClientRect();
                  const feedbackBoxRect = feedbackBox.getBoundingClientRect();

                  // If feedback box is taking up too much space, adjust scroll
                  if (feedbackBoxRect.bottom > chatboxRect.bottom - 20) {
                    chatbox.scrollTop = chatbox.scrollHeight - chatbox.clientHeight;
                  }
                }
              }, 100);
            }
          });
        }
      }
    },
    resetThumbs() {
      this.userClickedThumbsUp = false;
      this.userClickedThumbsDown = false;
      this.feedbackSubmitted = false;
    },

    async sendMessage(msg) {
      this.disableListSemantics();
      await this.$nextTick();
      if (this.userClickedThumbsDown && !this.feedbackSubmitted) {
        await this.submitRating('bad', this);
        this.feedbackText = "";
        this.showFeedbackBox = false;
        this.source = "",
          // AICHATBOT-469 showSources auto on if academic bot
          this.showSources = this.$route.query.botName?.toLowerCase().startsWith("a_");
      }
      if (this.currChars <= this.charLimit) {
        this.resetThumbs();
        this.currentMsg = "";
        this.questionId = "";
        this.resetThumbs();
        if (!this.firstQuestionAsked) {
          this.firstQuestionAsked = true;
          this.sampleQuestionsVisible = false;
        }
        const maskedMsg = this.maskNineDigitNumbers(msg);
        const escapedMsg = escapeHtml(maskedMsg);
        this.messageAdded(escapedMsg,false);
        await this.$nextTick();
        await this.getMessage(escapedMsg);
      }else{
        console.log("Message exceeds character limit or is empty");
      }

    },
    messageAdded(msg, is_VA, is_greeting, sources, questionId, question, isLastInSeries = true, visualContent = null, visualTitle = null, adaptiveCardTemplate = null) {
      const announcementRole = is_VA ? 'bot' : 'user';
      let announcementContent = msg;

      if(is_VA) {
        let formattedMsg = this.addHyperlinks(msg, sources)
        console.log("formattedMsg after hyperlinking: ", formattedMsg)
        formattedMsg = this.maskNineDigitNumbers(formattedMsg)
        // AICHATBOT-469 showSources auto on if academic bot
        this.messages.push({
          message: formattedMsg,
          role: 'bot',
          isGreeting: is_greeting,
          sources: sources,
          showSources: (this.$route.query.botName?.toLowerCase().startsWith("a_") && !is_greeting && sources && Object.keys(sources).length > 0),
          questionId: questionId,
          question: question,
          answer: msg,
          feedbackText: '',
          userEmail:'',
          notifyOnResolution:false,
          isLastInSeries: isLastInSeries,
          artifacts: [],
          toolCalls: [],
          showToolCalls: false,
          visualContent: visualContent,
          visualTitle: visualTitle,
          adaptiveCardTemplate: adaptiveCardTemplate,
        });
        announcementContent = formattedMsg;
      } else {
        let growingChat = document.getElementById("growingChat");
        this.bot = is_VA ? this.bot : growingChat.clientHeight;
        this.messages.push({message:msg, role:'user'});
      }

      // Wait for Vue to update DOM, then for browser paint
      this.$nextTick(() => {
        this.announceToScreenReader(announcementRole, announcementContent);
      });

      setTimeout(() => {
        const chatbox = this.$refs.chatbox;
        if (chatbox) {
          chatbox.scrollTop = chatbox.scrollHeight;
        }
      }, 0)

      this.setupOAuthLinks();
    },

    async attemptReconnection() {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.showReconnectionError();
        return;
      }

      this.isReconnecting = true;
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

      // Exponential backoff: 2^attempt seconds
      const delay = Math.pow(2, this.reconnectAttempts) * 1000;

      this.reconnectTimeoutId = setTimeout(() => {
        this.connectToWebSocket(this.$route.query.botName, true);
      }, delay);
    },

    showReconnectionError() {
      this.errorMessage = "Connection lost. Please refresh the page to reconnect.";
      this.showErrorDialog = true;
    },

    async getMessage(msg) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.loading = true;
        this.messageBuffer = "";
        this.socket.send(JSON.stringify({
          type: "answer",
          data: msg
        }));
      } else {
        console.log("WebSocket is not connected. Attempting to reconnect...");
        this.attemptReconnection();
      }
    },

    cleanup() {
      clearInterval(this.pingIntervalId);
      clearTimeout(this.reconnectTimeoutId);
      this.clearToolStatusTimeout();
      if (this.liveRegionClearTimeout) {
        clearTimeout(this.liveRegionClearTimeout);
        this.liveRegionClearTimeout = null;
      }
      if (this.socket) {
        this.socket.close(1000, 'Component unmounting');
        this.socket = null;
      }
    },

    clearToolStatusTimeout() {
      if (this.toolStatusTimeout) {
        clearTimeout(this.toolStatusTimeout);
        this.toolStatusTimeout = null;
      }
    },

    handleToolMessage(data) {
      const { content, execution_id, status } = data;

      this.clearToolStatusTimeout();

      if (status === 'started') {
        // Track the start of a tool execution
        this.activeToolExecutions[execution_id] = {
          content: content,
          startTime: Date.now(),
          startTimestamp: new Date().toLocaleTimeString()
        };

        // Update current tool status for display
        this.toolStatus = content;
        if (content) {
          this.announceToScreenReader('bot', content);
        }

      } else if (status === 'completed') {
        // Tool execution completed successfully
        const execution = this.activeToolExecutions[execution_id];

        if (execution) {
          const endTime = Date.now();
          const runtime = endTime - execution.startTime;
          const runtimeSeconds = (runtime / 1000).toFixed(1);

          // Add completed tool call to current message's history
          this.currentToolCalls.push({
            content: execution.content,
            timestamp: execution.startTimestamp,
            runtime: `${runtimeSeconds}s`,
            execution_id: execution_id,
            status: 'completed'
          });

          if (execution.content) {
            this.announceToScreenReader('bot', `${execution.content} completed`);
          }

          // Clean up the active execution
          delete this.activeToolExecutions[execution_id];
        }
        else if (content) {
          this.announceToScreenReader('bot', content);
        }

        // Clear tool status after 3 seconds
        this.toolStatusTimeout = setTimeout(() => {
          this.toolStatus = "";
        }, 3000);

      } else if (status === 'error') {
        // Tool execution failed
        const execution = this.activeToolExecutions[execution_id];

        if (execution) {
          const endTime = Date.now();
          const runtime = endTime - execution.startTime;
          const runtimeSeconds = (runtime / 1000).toFixed(1);

          // Add failed tool call to current message's history
          this.currentToolCalls.push({
            content: execution.content,
            timestamp: execution.startTimestamp,
            runtime: `${runtimeSeconds}s`,
            execution_id: execution_id,
            status: 'error',
            errorMessage: content // The error message from the current data
          });

          const errorAnnouncement = execution.content
            ? `${execution.content} failed${content ? `: ${content}` : ''}`
            : content;
          if (errorAnnouncement) {
            this.announceToScreenReader('bot', errorAnnouncement);
          }

          // Clean up the active execution
          delete this.activeToolExecutions[execution_id];
        }
        else {
          const fallbackAnnouncement = content || 'Tool execution failed';
          this.announceToScreenReader('bot', fallbackAnnouncement);
        }

        // Clear tool status after 3 seconds
        this.toolStatusTimeout = setTimeout(() => {
          this.toolStatus = "";
        }, 3000);
      }
    },

    handleArtifact(data) {
      // Add artifact to buffer instead of directly to message
      const artifact = {
        data: data.data,
        filename: data.filename,
        filetype: data.filetype
      };
      this.artifactBuffer.push(artifact);
      console.log('Artifact added to buffer:', artifact.filename, 'Buffer size:', this.artifactBuffer.length);
    },

    attachBufferedArtifacts() {
      // Attach buffered artifacts to the most recent bot message
      console.log('Attempting to attach artifacts. Buffer length:', this.artifactBuffer.length, 'Messages length:', this.messages.length);
      if (this.artifactBuffer.length > 0 && this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];
        console.log('Last message role:', lastMessage.role);
        if (lastMessage.role === 'bot') {
          if (!lastMessage.artifacts) {
            lastMessage.artifacts = [];
          }
          lastMessage.artifacts.push(...this.artifactBuffer);
          console.log('Artifacts attached to message. Total artifacts on message:', lastMessage.artifacts.length);
          console.log('Artifact filenames:', lastMessage.artifacts.map(a => a.filename));
        }
        this.artifactBuffer = []; // Clear buffer after attaching
      }
    },

    attachToolCalls() {
      // Attach tool calls to the most recent bot message
      console.log('Attempting to attach tool calls. Tool calls length:', this.currentToolCalls.length, 'Messages length:', this.messages.length);
      if (this.currentToolCalls.length > 0 && this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];
        console.log('Last message role:', lastMessage.role);
        if (lastMessage.role === 'bot') {
          if (!lastMessage.toolCalls) {
            lastMessage.toolCalls = [];
          }
          lastMessage.toolCalls.push(...this.currentToolCalls);
          console.log('Tool calls attached to message. Total tool calls on message:', lastMessage.toolCalls.length);
        }
        this.currentToolCalls = []; // Clear buffer after attaching
      }
    },

    handleMessageResponse(data) {
      let answer = data.answer;
      let questionId = data.questionId;
      let sourceMap = data.source_map || {};
      let visualContent = data.visualContent || null;
      let visualTitle = data.visualTitle || null;
      let adaptiveCardTemplate = null;

      // Extract adaptive card blocks from answer (take last one found)
      if (typeof answer === 'string') {
        const regex = /```\s*adaptivecard\s*([\s\S]*?)```/gi;
        let match;
        while ((match = regex.exec(answer)) !== null) {
          try {
            adaptiveCardTemplate = JSON.parse(match[1].trim());
          } catch (e) {
            console.warn('AdaptiveCard JSON parse error:', e.message);
          }
        }
        // Remove all adaptive card blocks from answer
        answer = answer.replace(regex, '').trim();
      }

      // In the interest of not breaking this 2k line code turn the new citation format into the old.
      const normalized = this.normalizeCitations(answer, sourceMap);
      answer = normalized.text;
      sourceMap = normalized.sourceMap;

      let msg = data.msg || "";
      this.sources = sourceMap;
      const isGreeting = /^(hello|hi|hey|greetings)$/i.test(msg.trim());

      if (typeof answer === 'object' && !Array.isArray(answer) && answer !== null && !answer.question) {
        // Filter to only entries with non-empty content
        const entries = Object.entries(answer);
        const nonEmptyEntries = entries.filter(([_, value]) => value !== "");

        // Check if there are any non-empty responses
        if (nonEmptyEntries.length === 0) {
          // If all responses are empty, display a generic message
          const genericResponse = "I'm sorry, none of the requested chatbots had a verified answer to your question. Please try rephrasing your question or asking something else.";
          this.messageAdded(genericResponse, true, false, {}, questionId, msg, true);
        } else if (isGreeting && nonEmptyEntries.length > 0) {
          // For "hello", just use the first non-empty response
          const [key, responseText] = nonEmptyEntries[0];
          let currentSourceMap = sourceMap[key] || {};

          try {
            let processedResponse = this.processLaTeXAndMarkdown(responseText);
            // For hello, don't add the bot intro line
            this.messageAdded(processedResponse, true, false, currentSourceMap, questionId, msg, true);
          } catch (e) {
            console.error(`Error processing greeting response:`, e);
            this.messageAdded('Sorry, I had trouble processing your greeting.', true, false, {}, questionId, msg, true);
          }
        } else {
          // Combine all responses into one message
          let combinedResponse = "";
          let combinedSourceMap = {};
          let globalSourceCounter = 1;

          // Process only non-empty responses
          for (let i = 0; i < nonEmptyEntries.length; i++) {
            const [key, responseText] = nonEmptyEntries[i];
            let currentSourceMap = sourceMap[key] || {};
            let modifiedResponseText = responseText;

            // Create a mapping of original source numbers to global sequential numbers
            const botSourceMapping = {};
            if (Object.keys(currentSourceMap).length > 0) {
              Object.keys(currentSourceMap).forEach(originalSourceNum => {
                const sourceInfo = currentSourceMap[originalSourceNum];
                botSourceMapping[originalSourceNum] = globalSourceCounter;

                // Add this source to combined map with the new global number
                combinedSourceMap[globalSourceCounter] = sourceInfo;

                // Add a bot identifier to the source info
                combinedSourceMap[globalSourceCounter].botName = key;

                globalSourceCounter++;
              });
            }

            // Rename citation references in the response text
            if (Object.keys(botSourceMapping).length > 0) {
              Object.keys(botSourceMapping).forEach(origNum => {
                const globalNum = botSourceMapping[origNum];
                const sourceRegex = new RegExp(`\\[${origNum}\\]`, 'g');
                modifiedResponseText = modifiedResponseText.replace(sourceRegex, `[${globalNum}]`);
              });
            }

            // Process regular text response with updated citation numbers
            let processedResponse = this.processLaTeXAndMarkdown(modifiedResponseText);
            const formattedResponse = `<div class="bot-intro"><strong>${this.botNameMapping[key]}</strong>'s Response:</div>${processedResponse}`;

            // Add to combined response with appropriate spacing
            if (combinedResponse !== "") {
              combinedResponse += "<hr class='response-divider' />";
            }
            combinedResponse += formattedResponse;
          }

          // Add the combined message once with the properly combined source map
          this.messageAdded(combinedResponse, true, false, combinedSourceMap, questionId, msg, true);
        }
      } else {
        // Handle single response (existing logic)
        try {
          if (Array.isArray(answer) && answer.length > 0 && answer[0].question) {
            this.appendQuiz(answer, questionId);
            return;
          }
          if (typeof answer === 'object' && answer.question) {
            this.appendQuiz([answer], questionId);
            return;
          }

          let processedResponse = this.processLaTeXAndMarkdown(answer);
          console.log("processedResponse for markdown: ", processedResponse)
          this.messageAdded(processedResponse, true, false, sourceMap, questionId, msg, true, visualContent, visualTitle, adaptiveCardTemplate);
        } catch (e) {
          console.info('not JSON Quiz', e.message);
          let processedResponse = this.processLaTeXAndMarkdown(answer);
          this.messageAdded(processedResponse, true, false, sourceMap, questionId, msg, true, visualContent, visualTitle, adaptiveCardTemplate);
        }
      }

    },

    stripHtmlTags(html) {
      if (!html) return '';
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    },

    async submitRating(rating, message) {
      console.log("submitRating called");
      console.log("rating:", rating)
      console.log("message:", message)

      // Prevent multiple submissions
      if (message.isSubmittingFeedback) {
        console.log("Rating submission already in progress");
        return;
      }

      message.feedbackError = '';
      message.emailError = '';
      if (rating && message.question !== '' && message.answer !== '') {

        if (rating === 'bad' && !message.feedbackText.trim()) {
          message.feedbackError = 'Please provide feedback';
          return;
        }

        // Validate email if notification is requested

        if (message.notifyOnResolution) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!message.userEmail.trim()) {
            message.emailError = 'Please enter an email address';
            return;
          } else if (!emailRegex.test(message.userEmail.trim())) {
            message.emailError = 'Please enter a valid email address';
            return;
          }
        }

        const pair = message.question + message.questionId + message.answer;
        if (this.submittedPairs.includes(pair)) {
          // If it has been submitted before, don't make the API call again
          console.log("Rating already submitted for this pair.");
          return;
        }

        try {
           // Set loading state to disable button and show loading text
          message.isSubmittingFeedback = true;
          console.log("submitRating called with msg:","rating:", rating);

          let response = await this.$axios.post(
            `/rating/${this.$route.query.botName}`,
            {
              sessionId: this.sessionId,
              questionId: message.questionId,
              answer: message.answer,
              rating: rating,
              feedback: message.feedbackText,
              email: message.notifyOnResolution ? message.userEmail : ''
              // source: this.source
            }
          );

          console.log(response);
          this.submittedPairs.push(pair);
          message.feedbackSubmitted = true;
          this.feedbackText = "";
          message.showFeedbackBox = false;

          if (message.notifyOnResolution && message.userEmail) {
            this.sessionEmail = message.userEmail;
          }

        } catch (error) {
          console.error(error);
                  // Re-enable button on error so user can retry
          message.isSubmittingFeedback = false;
        } finally {
          // Always reset loading state after request completes
          if (message.feedbackSubmitted) {
            message.isSubmittingFeedback = false;
          }
        }
      }
    },

    processLaTeXAndMarkdown(res) {
      try {
        let delimiters = [
          {left: "$$", right: "$$", display: true},
          {left: "\\(", right: "\\)", display: false},
          {left: "\\begin{equation}", right: "\\end{equation}", display: true},
          {left: "\\begin{align}", right: "\\end{align}", display: true},
          {left: "\\begin{alignat}", right: "\\end{alignat}", display: true},
          {left: "\\begin{gather}", right: "\\end{gather}", display: true},
          {left: "\\begin{CD}", right: "\\end{CD}", display: true},
          {left: "\\[", right: "\\]", display: true},
        ]

        // Pre-process the text to ensure URLs are properly handled
        // 1. Add a space after URLs that are followed immediately by a newline
        res = res.replace(/(https?:\/\/[^\s]+)(\n)/g, '$1 $2');

        // 2. Ensure that URLs ending with HTML tags are properly separated
        res = res.replace(/(https?:\/\/[^\s<>]+)(<\/[a-z]+>)/gi, '$1 $2');

        // 3. Handle citations on their own line - pull them up to the previous line
        res = res.replace(/([^\n])\n(\[\d+\](?:\s*,\s*\[\d+\])*)(\s*\n|$)/g, '$1 $2$3');

        let split = splitAtDelimiters(res, [...delimiters, {left: '`', right: '`', display: true}]);
        let hybridText = "";
        let mathBlocks = [];
        for (let i = 0; i < split.length; i++) {
          let chunk = split[i];
          if (chunk.type === 'text') {
            hybridText += chunk.data;
          } else if (chunk.type === 'math') {
            if (chunk.rawData.startsWith("`") && chunk.rawData.endsWith("`")) {
              hybridText += chunk.rawData;
            } else {
              mathBlocks[i] = katex.renderToString(chunk.data, {
                delimiters: delimiters,
                displayMode: chunk.display
              });
              hybridText += '`__KATEX__' + i + '__`';
            }
          }
        }

        hybridText = converter.makeHtml(hybridText);

        // Post-processing cleanup for any URLs that might still have HTML elements embedded
        // 1. Fix URLs with <br> tags
        hybridText = hybridText.replace(/<a href="([^"]*)<br[^>]*>([^"]*)">/g, '<a href="$1$2">');

        // 2. Fix URLs with closing tags embedded in them
        hybridText = hybridText.replace(/<a href="([^"]*)<\/a>([^"]*)">/g, '<a href="$1$2">');

        // 3. More general approach to clean any HTML tags in URLs
        hybridText = hybridText.replace(/<a href="([^"]*)(<[^>]+>)([^"]*)">/g, '<a href="$1$3">$2');

        // 4. Handle citation references that got separated with <br> tags
        hybridText = hybridText.replace(/([^>])<br\s*\/?>\s*(\[\d+\](?:\s*,\s*\[\d+\])*)/g, '$1 $2');

        for (let i = 0; i < mathBlocks.length; i++) {
          hybridText = hybridText.replace('<code>__KATEX__' + i + '__</code>', mathBlocks[i]);
        }

        if (hybridText.includes('katex') || hybridText.includes('<code')) {
          hybridText = '<h3 class="disclaimer">Code/equations generated by AI may not be fully accurate; please verify content with course materials.</h3>' + hybridText;
        }

        return hybridText ? `<div class="markdown">${hybridText}</div>` : res;
      } catch (e) {
        console.log("Error processing LaTeX/md:", e);
        return res;
      }
    },

    announceStatus(text) {
      if (!text) {
        return;
      }
      this.queueAnnouncement(text);
    },
    announceToScreenReader(role, rawContent) {
      const announcement = this.buildAnnouncement(role, rawContent);
      if (announcement) {
        this.queueAnnouncement(announcement);
      }
    },
    buildAnnouncement(role, rawContent) {
      const content = this.extractAnnouncementContent(rawContent);
      const prefix = role === 'bot' ? this.srBotLabel : this.srUserLabel;
      if (prefix && content) {
        return `${prefix}. ${content}`;
      }
      return content || prefix;
    },
    extractAnnouncementContent(rawContent) {
      let content = '';
      if (Array.isArray(rawContent)) {
        content = rawContent.join(' ');
      } else if (typeof rawContent === 'string') {
        content = this.stripHtml(rawContent);
      } else if (rawContent && typeof rawContent === 'object') {
        if (typeof rawContent.message === 'string') {
          content = this.stripHtml(rawContent.message);
        } else if (typeof rawContent.text === 'string') {
          content = this.stripHtml(rawContent.text);
        }
      }
      return content.replace(/\s+/g, ' ').trim();
    },
    queueAnnouncement(text) {
      if (!text) {
        return;
      }
      const trimmed = text.trim();
      const now = Date.now();
      if (trimmed && trimmed === this.lastAnnouncementText && now - this.lastAnnouncementTimestamp < 750) {
        return;
      }
      const chain = this.announcementChain || Promise.resolve();
      this.announcementChain = chain
        .catch(() => {})
        .then(() => this.performAnnouncement(text));
    },
    async performAnnouncement(text) {
      this.liveRegionText = '';
      await this.waitForAnnouncementFrame();
      this.liveRegionText = text;
      this.lastAnnouncementText = (text || '').trim();
      this.lastAnnouncementTimestamp = Date.now();
      this.scheduleLiveRegionClear(text);
    },
    waitForAnnouncementFrame() {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    },
    scheduleLiveRegionClear(expectedText) {
      if (this.liveRegionClearTimeout) {
        clearTimeout(this.liveRegionClearTimeout);
      }
      this.liveRegionClearTimeout = setTimeout(() => {
        if (this.liveRegionText === expectedText) {
          this.liveRegionText = '';
        }
        this.liveRegionClearTimeout = null;
      }, 2500);
    },

    handleGlobalKeydown(event) {
      this.listSemanticsEnabled = false;
      if (!this.initialLoadingComplete) {
        return;
      }
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (this.isInteractiveEventTarget(event.target)) {
        return;
      }
      const key = event.key;
      if (!key || key.length !== 1) {
        return;
      }
      const textarea = this.getActiveChatInput();
      if (!textarea) {
        return;
      }
      event.preventDefault();
      this.focusChatInput(textarea);
      this.insertCharacterAtCaret(textarea, key);
    },
    isInteractiveEventTarget(target) {
      if (!(target instanceof Element)) {
        return false;
      }
      return Boolean(target.closest('input, textarea, select, button, [role="textbox"], [contenteditable="true"], a[href], summary'));
    },
    getActiveChatInput() {
      const secondary = this.$el.querySelector('#chat-message-2');
      if (secondary && !secondary.closest('[aria-hidden="true"]')) {
        return secondary;
      }
      const primary = this.$el.querySelector('#chat-message');
      return primary || null;
    },
    focusChatInput(input) {
      this.listSemanticsEnabled = false;
      const target = input || this.getActiveChatInput();
      target?.focus();
    },
    insertCharacterAtCaret(input, char) {
      const value = input.value || '';
      const start = input.selectionStart ?? value.length;
      const end = input.selectionEnd ?? value.length;
      const nextValue = value.slice(0, start) + char + value.slice(end);
      input.value = nextValue;
      const newPos = start + char.length;
      input.setSelectionRange(newPos, newPos);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    },

    buildOptionalLoginLabel(isLoggedIn) {
      if (isLoggedIn && this.umName) {
        return `You are logged in as ${this.umName}.`;
      }
      const translated = typeof this.$t === 'function' ? this.$t('message.optional_login_prompt') : '';
      if (translated && translated !== 'message.optional_login_prompt') {
        return translated;
      }
      return 'Sign in for a more personalized experience.';
    },
    buildSampleQuestionsLabel(questions = []) {
      if (!Array.isArray(questions) || !questions.length) {
        return 'Department sample questions are available.';
      }
      const cleaned = questions
        .slice(0, 3)
        .map((question) => this.stripHtml(question).trim())
        .filter(Boolean);
      const summary = cleaned.join('. ');
      const remainingCount = questions.length - cleaned.length;
      const remainder = remainingCount > 0 ? `Plus ${remainingCount} more questions.` : '';
      return `Department sample questions. ${summary}${remainder ? ` ${remainder}` : ''}`;
    },

    getFirstWords(text, numWords) {
      const words = text.split(/\s+/);
      return words.slice(0, numWords).join(' ');
    },
    getRemainingText(text, numWords) {
      const words = text.split(/\s+/);
      return words.length > numWords ? words.slice(numWords).join(' ') : '';
    },
    onKeydown(event) {
      if (event.key === 'Enter') {
        if (!event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.sendMessage(this.currentMsg);
        }
      }
    },

    handleVisualizationCopied() {
      console.log('Visualization code copied to clipboard');
      // You could add a toast notification here if you have a notification system
    },

    handleVisualizationDownloaded() {
      console.log('Visualization downloaded');
      // You could add a toast notification here if you have a notification system
    },

    handleOAuthComplete(event) {
      if (event.data?.type === 'oauth-complete' && event.data?.ok === true) {
        for (let i = this.messages.length - 1; i >= 0; i--) {
          if (this.messages[i].role === 'user') {
            this.sendMessage(this.messages[i].message);
            break;
          }
        }
      }
    },

    setupOAuthLinks() {
      this.$nextTick(() => {
        this.$nextTick(() => {
          const links = this.$el.querySelectorAll('a[href$="/oauth/start-page"]:not([data-oauth-setup])');
          links.forEach(link => {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              window.open(link.href, '_blank', 'popup,width=600,height=700,scrollbars=yes');
            });
            link.setAttribute('data-oauth-setup', 'true');
          });
        });
      });
    },
  },
  mounted(){
    document.addEventListener('keydown', this.handleGlobalKeydown, true);
    this.sizing();
    this.sessionId = this.$uuidv4();
    this.token = "V8qnelCt3LIn4Gi6";
    this.connectToWebSocket(this.$route.query.botName);
    window.addEventListener('message', this.handleOAuthComplete);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleGlobalKeydown, true);
    this.cleanup();
    window.removeEventListener('message', this.handleOAuthComplete);
  },
};
</script>
<style>

html, body {
  overflow-y: hidden;
}

#fullBox {
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  /* Ensure the chat input stays at the bottom */
  position: relative;
}

#messages {
  overflow-y: auto;
  overflow-x: auto;
  height: 425px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chat-message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
}

.chat-message-item {
  outline: none;
}

#userMsg {
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
}

#growingChat {
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
}
p {
  margin-bottom: 16px;
}

#row {
  width: 100%;
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
}


#feedbackBox {
  border: 1px solid black; /* Border around the entire box */
  padding: 2px; /* Space inside the box */
  display: flex;
  flex-direction: column; /* Align children in a column */
  align-items: flex-end;
  border-radius: 5px;
  align-items: stretch;
}

#feedbackBox textarea {
  width: calc(100% - 5px); /* Adjust width to account for padding */
  border: none; /* Remove border from the textarea */
  outline: none; /* Remove the outline from the textarea */
  resize: none; /* Optional: prevent resizing */
  padding: 2px 0px;
}

.submit-row {
  display: flex;
  justify-content: space-between; /* Aligns children (char count and button) on opposite ends */
  align-items: center; /* Aligns children vertically in the center */
  margin-top: 2px; /* Optional: adds space above the row */
}

.send-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

#send-button {
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
  height: 50px;
  width: 50px;
  font-size: 18px;
  margin-bottom: 1px;
}

.char-count {
  margin-left: 10px;
  color: #666666;
  font-style: italic;
}

.char-count-small {
  font-size: 11px;
  color: #666666;
  text-align: center;
}

#submitButton {
  width: 70px; /* Button width to fill the container */
  padding: 10px 0; /* Padding for the button */
  border: none; /* Optional: style as needed */
  background-color: #e21833; /* Optional: background color */
  color: white; /* Text color */
  cursor: pointer; /* Cursor on hover */
  border-radius: 5px;
}

.userMsgBox {
  height: 65px;
  border-bottom-left-radius: 8px;
}

.sendMsgBox {
  padding-top: 20px;
}

.sendMsgBox .v-input__append{
  margin-inline-start: 0 !important;
}

.userText {
  /* color: white; */
  font-size: 14px;
  font-weight: normal;
  text-align: right;
  clear: both;
}

.userText > .message-bubble {
  line-height: 1.5em;
  display: inline-block;
  background: #406770;
  padding: 10px;
  border-radius: 8px 8px 2px 8px;
  max-width: 80%;
  margin-right: 10px;
  animation: floatup .5s forwards
}
.userText > .message-bubble.dark-theme {
  background: #4B4B4D;  /* Dark gray from UMD palette */
  color: #FFFFFF;
}

.botText > .message-bubble.dark-theme {
  background: #2F3033;  /* Darker gray from UMD palette */
  color: #FFFFFF;
}

.feedback-box.dark-theme {
  background-color: #2F3033;
  color: #E0E0E0;
}

.feedback-box.dark-theme textarea {
  background-color: #4B4B4D;
  color: #FFFFFF;
  border-color: #666666;
}

.feedback-box.dark-theme .email-input {
  background-color: #4B4B4D;
  color: #FFFFFF;
  border-color: #666666;
}

/* Update submit button for dark mode */
.submit-button.dark-theme {
  background-color: #FF6B6B; /* UMD's light red for dark mode */
  color: #000000;
}

.sourceBox.dark-theme {
  background-color: #2F3033;
  color: #FFFFFF;
  border-color: #666666;
}

.sourceBox.dark-theme .source-header {
  border-bottom-color: #666666;
}

.sourceBox.dark-theme .close-button {
  color: #FF6B6B !important;
}

.botText {
  /* color: #000; */
  font-weight: normal;
  font-size: 14px;
  text-align: left;
}

p.botText > .message-bubble {
  line-height: 1.5em;
  display: inline-block;
  padding: 10px;
  border-radius: 8px 8px 8px 2px;
  max-width: 80%;
  margin-left: 10px;
  animation: floatup .5s forwards
}

.bot-intro {
  font-weight: 500;
  font-style: italic;
  color: #666;
  margin-bottom: 8px;
  font-size: 0.9em;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}

.dark-theme .bot-intro {
  color: #aaa;
  border-bottom-color: #444;
}

.rating-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.instructions {
  font-size: 0.85em;
  color: #666;
  margin-left: 28px;
  font-style: italic;
  line-height: 1.4;
}

.source-icon {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.copy {
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
  float: right;
  border: none;
  background: none;
  padding: 2px 5px;
  font-size: 13px;
}

.spinner {
  text-align: center;
  padding: 10px;
}

.typing-indicator {
  display: flex;
  align-items: center;
  justify-content: left;
  height: 30px;
  gap: 4px;
  margin-left: 10px;
  flex-wrap: wrap;
}

.thinking-testudo {
  width: 26px;
  height: 30px;
  margin-right: 8px;
}

.tool-status {
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-right: 8px;
  animation: pulse 2s infinite;
  transition: opacity 0.5s ease-out;
  white-space: nowrap;
}

.dark-theme .tool-status {
  color: #E0E0E0;
}

@keyframes pulse {
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
}

.dot {
  font-size: 16px;
  border-radius: 50%;
  opacity: 0.2;
  animation: blink 1.4s infinite;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}
.dot:nth-child(2) {
  animation-delay: 0.2s;
}
.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0% { opacity: 0.2; transform: scale(1); }
  20% { opacity: 1; transform: scale(1.3); }
  100% { opacity: 0.2; transform: scale(1); }
}

.thumb-up, .thumb-down, .copy {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 2px 5px;
  font-size: 10px;
}

/* Set a specific size for the SVG icons */
.thumb-up svg,
.thumb-down svg,
.copy svg {
  width: 16px;
  height: 16px;
}

/* Hover states - scale the SVG, not the button */
.thumb-up:hover svg {
  color: green;
  transform: scale(1.2);
}

.thumb-down:hover svg {
  color: #e21833;
  transform: scale(1.2);
}

.copy:hover svg {
  color: blue;
  transform: scale(1.2);
}

/* Clicked states */
.thumb-up.clicked svg {
  color: green;
}

.thumb-down.clicked svg {
  color: red;
}

/* Tool Calls Styling */
.tool-calls-container {
  margin: 4px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
  overflow: hidden;
  font-size: 12px;
}

.tool-calls-container.dark-theme {
  border-color: #444;
  background-color: #333;
}

.tool-calls-header {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #f0f0f0;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #ddd;
}

.tool-calls-header:hover {
  background-color: #e8e8e8;
}

.tool-calls-container.dark-theme .tool-calls-header {
  background-color: #2a2a2a;
  border-bottom-color: #444;
}

.tool-calls-container.dark-theme .tool-calls-header:hover {
  background-color: #3a3a3a;
}

.tool-calls-icon {
  font-size: 12px;
  margin-right: 6px;
}

.tool-calls-title {
  font-size: 11px;
  font-weight: 500;
  color: #666;
  flex-grow: 1;
}

.tool-calls-container.dark-theme .tool-calls-title {
  color: #ccc;
}

.tool-calls-toggle {
  font-size: 10px;
  color: #999;
  transition: transform 0.2s;
}

.tool-calls-toggle.expanded {
  transform: rotate(180deg);
}

.tool-calls-list {
  max-height: 150px;
  overflow-y: auto;
}

.tool-call-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 8px;
  border-bottom: 1px solid #eee;
  font-size: 11px;
}

.tool-call-item:last-child {
  border-bottom: none;
}

.tool-calls-container.dark-theme .tool-call-item {
  border-bottom-color: #444;
}

.tool-call-text {
  flex-grow: 1;
  margin-right: 6px;
  color: #555;
}

.tool-calls-container.dark-theme .tool-call-text {
  color: #ddd;
}

.tool-call-time {
  color: #999;
  font-size: 10px;
  white-space: nowrap;
}

.tool-calls-container.dark-theme .tool-call-time {
  color: #aaa;
}

/* Dark theme adjustments */
.dark-theme .thumb-up:hover svg {
  color: #90EE90;
}

.dark-theme .thumb-down:hover svg {
  color: #FF6B6B;
}

.dark-theme .copy:hover svg {
  color: #89CFF0;
}

.dark-theme .thumb-up.clicked svg {
  color: #90EE90;
}

.dark-theme .thumb-down.clicked svg {
  color: #FF6B6B;
}
.user-icon {
  position: relative;
  top: 12px;
  width: 25px;
  height: 25px;
  color: #333333; /* Default color for light theme */
}

.dark-theme-icon {
  color: #E0E0E0; /* Light color for dark theme */
}

.sample-questions-card.dark-theme {
  background-color: #2F3033;
  color: #FFFFFF;
}

.suggested-questions-container.dark-theme {
  color: #FFFFFF;
}

.suggested-questions-icon.dark-theme {
  color: #FFFFFF;
}

.v-list-item-subtitle {
  color: #666666;
  opacity: 0;
}

.rounded-message-input.dark-theme {
  background-color: #4B4B4D;
  border-color: #666666;
}

.dark-theme #send-button {
  color: #FFFFFF;
}

.dark-theme .char-count-small {
  color: #CCCCCC;
}

.dark-theme .markdown {
  color: #E0E0E0;
}

.dark-theme .markdown blockquote {
  background-color: #4B4B4D;
  border-color: #666666;
}

/* Dark theme for error messages */
.dark-theme .error-message {
  color: #FF6B6B;
}

.citations {
  border: none;
  background: none;
  color: darkgoldenrod;
  padding-top: 10px;
  font-size: 25px;
}

.copy:hover {
  color: blue;
  font-size: 20px;
}

.sourceBox {
  position: relative;
  border: 1px solid black; /* Border around the entire box */
  padding: 2px; /* Space inside the box */
  display: flex;
  flex-direction: column; /* Align children in a column */
  align-items: flex-end;
  border-radius: 5px;
  align-items: stretch;
}

.close-button {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 15px;
  font-weight: bold;
  border: none;
  background: none;
  cursor: pointer;
  color: #e21833 !important;

}

.source-header {
  display: flex; /* Use flexbox for layout */
  justify-content: space-between; /* Space between the title and the close button */
  align-items: center; /* Center items vertically */
}

.source-header span {
  padding: 5px 0 0;
}

.source-list {
  padding-bottom: 5px;
  margin-left: 10px;
}

.tooltip {
  position: absolute;
  top: -10px;
  border: 1px solid #ccc; /* Optional border */
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
  text-transform: capitalize;
  font-size: 11px;
  opacity: 0.75;
  background-color: #323232;
  color: #fff;
  padding: 4px;
  border-radius: 3px;
  display: block;
}
::placeholder {
  color: #888888; /* Gray text */
}

.response-divider {
  margin: 20px 0;
  border: 0;
  border-top: 1px solid #eee;
}

/* For dark theme */
.dark-theme .response-divider {
  border-top-color: #444;
}


.source-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.25s;
}
.source-tooltip.show {
  opacity: 1;
}
.clickable-number {
  display: inline;
  cursor: pointer;
  color: blue;
  text-decoration: underline;
}

.botText span.clickable-number {
  padding: 0px;
  margin-left: 4px;
  cursor: pointer;
  text-decoration: underline;
}

.dark-theme .clickable-number {
  color: #89CFF0;  /* Light blue color for better visibility in dark mode */
}

/* For source numbers in dark mode */
.dark-theme #sourcesContainer .source-number {
  color: #89CFF0;  /* Light blue color for better visibility in dark mode */
}

#sourcesContainer .source-number {
  padding: 0;
}

#sourcesContainer .source-title {
  color: inherit;
  text-decoration: none;
}

#sourcesContainer .source-title.clickable { color: blue; text-decoration: underline; cursor: pointer; }

.dark-theme #sourcesContainer .source-number,
.dark-theme #sourcesContainer .source-title.clickable {
  color: #89CFF0;
}

#sourcesContainer .video-icon {
  font-size: 12px;
  padding-left: 7px;
  padding-bottom: 1px;
  color: #636363;
}

#sourcesContainer .doc-icon {
  font-size: 12px;
  padding-left: 7px;
  padding-bottom: 1px;
  color: #636363;
}

.reopen-icon-container {
  position: relative;
  bottom: 10px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-wrap: wrap;
  justify-content: right;
}

.reopen-icon {
  background-color: #e21833;
  color: white;
  border: none;
  border-radius: 50%;
  padding: 5px;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.reopen-icon svg {
  width: 18px;
  height: 18px;
  position: relative;
  top: 3px; /* Fine-tune vertical position */
}

.reopen-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}

.reopen-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}


.sample-questions-row {
  display: flex;
  flex-wrap: wrap;
}

.sample-question-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.botText.sample-questions-message {
  margin-top: 20px; /* Increase this value for more spacing */
}

.sample-question-card .v-card-title {
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  word-wrap: break-word;
  padding-bottom: 4px;
}

.sample-question-card .v-card-subtitle {
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  word-wrap: break-word;
  padding-bottom: 8px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Override the margin-inline-start property for v-input__append */
.v-input__append {
  margin-inline-start: 0 !important;
}

@media (max-width: 600px) {
  .centered-content {
    padding: 10px;
  }
}


div .markdown{
  white-space: normal;
  line-height: 1.5;

  h1, h2, h3, ul, ol, li, p  {
    margin: 0;
    margin-bottom: .25rem;
    padding: 0;
    line-height: 1.5;
  }

  ul, ol, li {
    padding-left: 1rem;
  }

  blockquote {
    /* Basic blockquote styles */
    margin: 5px;
    padding: 10px;
    background-color: #f0f8ff; /* Light blue background */
    border-radius: 15px; /* Rounded corners */
    border: 1px solid #ddd; /* Light border */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Subtle shadow */
  }

}

.rounded-message-input {
  border-radius: 20px !important;
  background-color: #f5f5f5;
  border: 1px solid #ddd;

}

.sample-questions-card {
  margin-bottom: 20px; /* Add some space between the cards */
}
/* Remove default styles from v-textarea */
.rounded-message-input >>> .v-textarea {
  background-color: transparent;
}

.rounded-message-input >>> .v-textarea__control textarea {
  padding: 8px 0; /* Adjust padding as needed */
}
.centered-content {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  padding-top: 100px;
  overflow-y:auto;
}
.content-wrapper {
  max-height: 100%;
  overflow-y: auto;
}

.v-card__title .v-btn.v-btn--icon {
  position: absolute;
  top: 8px;
  right: 8px;
}
.v-card__title .v-btn.v-btn--icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}


.close-btn {
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.chat-input-container {
  width: 100%;
  margin: 0 auto;
  /* Ensure the chat input container maintains proper positioning */
  position: relative;
  z-index: 10;
}

.v-field__field {
  box-shadow: none !important;
}

.v-field__outline {
  --v-field-border-width: 0 !important;
}

/* If you're using Vuetify 2, use this instead */
.v-text-field__slot::before,
.v-text-field__slot::after {
  display: none !important;
}
.suggested-questions-title {
  color: #666666 ; /* Light gray color */
  opacity: 1; /* Reduce opacity */
  user-select: none; /* Prevent text selection */
  cursor: default; /* Use default cursor */
}

.suggested-questions-container {
  display: flex;
  flex-direction: column;
}

.suggested-questions-icon {
  font-size: 16px; /* Adjust size as needed */
  color: #888; /* Light gray color to match disabled look */
  margin-right: 8px; /* Space between icon and text */
}

.title-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.v-input--density-default{
  --v-input-padding-top: 20px;
}

#chat-message::placeholder,
#chat-message-2::placeholder {
  font-style: normal;
  font-size: 16px;
}

.feedback-box textarea::placeholder,
.feedback-box .email-input::placeholder{
  font-style: normal;
}

.feedback-box {
  /* background-color: #f8f9fa; */
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 10px;
  margin-bottom: 10px;
  overflow: hidden;
  transition: all 0.3s ease;
  /* Ensure feedback box doesn't interfere with chat input positioning */
  position: relative;
  z-index: 1;
}

.feedback-content {
  padding: 15px;
}

.feedback-box textarea {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;
}

.feedback-box textarea:focus {

  outline: 0;

}

.feedback-footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.char-count {
  font-size: 12px;
  color: #6c757d;
}

.email-notification {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  user-select: none;
}
.dark-theme .checkbox-container {
  color: #E0E0E0;  /* This will change the text color in dark mode */
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  height: 18px;
  width: 18px;
  background-color: #eee;
  border-radius: 3px;
  margin-right: 8px;
  display: inline-block;
  position: relative;
  border: 1px solid #7f7f7f; /* Add this line for the border */
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #ccc;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #7f7f7f;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.email-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}


.submit-button {
  align-self: flex-end;
  background-color: #e21833;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #c01628;
}

.submit-button:disabled {
  background-color: #999999;
  opacity: 0.6;
}

.submit-button:disabled:hover {
  background-color: #999999;
}

.error-message {
  color: #e21833;
  font-size: 12px;
  margin-top: 5px;
}

.dark-theme .v-textarea textarea {
  color: #E0E0E0 !important;
}

.error-title {
  background-color: #e21833;
  color: white;
  padding: 12px;
}

.error-icon {
  margin-right: 10px;
}

.error-dialog .v-card__title {
  word-break: break-word;
}

/* Dark mode styles */
.dark-theme .error-title {
  background-color: #9a0e1e;
}

.dark-theme .v-card {
  background-color: #2F3033;
  color: #FFFFFF;
}

.dark-theme .v-btn {
  color: #FFFFFF;
}

/* Update the existing sample-question-item class */
.sample-question-item {
  position: relative;
  padding: 8px 35px 8px 10px;
  margin: 5px 0;
  border-radius: 5px;
  background-color: #454545;
  border: none;
  width: 100%;
  text-align: left;
  font: inherit;
  /* border-left: 4px solid #e21833; Red accent on the left side */
  cursor: pointer;
  transition: all 0.2s ease;
}

.sample-question-item *,
.botText span .sample-questions-list .sample-question-item * {
  color: white !important;
}

/* Make hover state slightly darker */
.sample-question-item:hover {
  background-color: #c01628; /* Slightly darker red on hover */
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Active state */
.sample-question-item:active {
  transform: translateY(0);
  background-color: #a01220; /* Even darker when clicked */
}

/* Focus state for accessibility */
.sample-question-item:focus {
  outline: 2px solid #fff;
  outline-offset: 2px;
  box-shadow: 0 0 0 4px rgba(226, 24, 51, 0.4);
}

/* Update the question indicator (arrow) color for better visibility on red */
.question-indicator {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  opacity: 0.9;
  color: white; /* Make arrow white for visibility on red background */
}

/* Dark theme adjustments */
.dark-theme .sample-question-item {
  background-color: #e21833; /* Keep the same red in dark mode */
}

.dark-theme .sample-question-item:hover {
  background-color: #c01628;
}

.dark-theme .sample-question-item:focus {
  outline-color: #fff;
}

.language-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dark-theme .language-loading-overlay {
  background-color: rgba(0, 0, 0, 0.8);
}

.language-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.language-loading-spinner p {
  font-size: 16px;
  color: inherit;
}

.reconnect-subtext {
  font-size: 0.9em;
  color: #666;
  margin-top: 8px;
  font-style: italic;
}

.dark-theme .reconnect-subtext {
  color: #ccc;
}

/* Table styles for markdown content */
.markdown table {
  border-collapse: collapse;
  width: 100%;
  margin: 16px 0;
}

.markdown th,
.markdown td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.markdown th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.dark-theme .markdown th {
  background-color: #4B4B4D;
  border-color: #666;
}

.dark-theme .markdown td {
  border-color: #666;
}

/* Tool call runtime display styles */
.tool-call-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  margin: 4px 0;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.02);
}

.tool-call-item.tool-call-error {
  background-color: rgba(220, 53, 69, 0.1);
  border-left: 3px solid #dc3545;
}

.dark-theme .tool-call-item {
  background-color: rgba(255, 255, 255, 0.05);
}

.dark-theme .tool-call-item.tool-call-error {
  background-color: rgba(220, 53, 69, 0.2);
}

.tool-call-content {
  flex: 1;
}

.tool-call-error-message {
  font-size: 0.85em;
  color: #dc3545;
  font-style: italic;
  margin-top: 4px;
}

.dark-theme .tool-call-error-message {
  color: #ff6b6b;
}

.tool-call-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9em;
  flex-shrink: 0;
}

.tool-call-runtime {
  color: #666;
  font-style: italic;
  font-weight: 500;
}

.dark-theme .tool-call-runtime {
  color: #aaa;
}

.tool-call-status-error {
  color: #dc3545;
  font-size: 1.1em;
}

.tool-call-status-success {
  color: #28a745;
  font-size: 1.1em;
  font-weight: bold;
}

.dark-theme .tool-call-status-error {
  color: #ff6b6b;
}

.dark-theme .tool-call-status-success {
  color: #40c954;
}
.sign-in-button {
  border: #2a2a2a solid 1px;
  border-radius: 4px;
  padding-left: 4px;
  padding-right: 4px;
  background-color: #f0f0f0;
}

/* Adaptive Card Container Styles */
.adaptive-card-container {

}

.dark-theme .adaptive-card-container {
  background-color: #2a2a2a;
  border-color: #444;
}

.adaptive-card-placeholder {
  color: #6c757d;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.dark-theme .adaptive-card-placeholder {
  color: #adb5bd;
}

/* Override AdaptiveCard default styles for better integration */
.adaptive-card-container :deep(.ac-adaptiveCard) {
  background: transparent !important;
  padding: 0 !important;
}

.adaptive-card-container :deep(.ac-pushButton) {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.adaptive-card-container :deep(.ac-pushButton:hover) {
  background-color: #0056b3;
}

.dark-theme .adaptive-card-container :deep(.ac-pushButton) {
  background-color: #ffc107;
  color: #000;
}

.dark-theme .adaptive-card-container :deep(.ac-pushButton:hover) {
  background-color: #ffb300;
}

.adaptive-card-container :deep(.ac-textBlock) {
  color: inherit;
}

.adaptive-card-container :deep(.ac-input) {
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 6px 12px;
}

.dark-theme .adaptive-card-container :deep(.ac-input) {
  background-color: #3a3a3a;
  border-color: #555;
  color: #fff;
}
</style>
